import axios, { AxiosInstance, AxiosError } from 'axios';

// const API_BASE_URL = (() => {
//   const envUrl = process.env.REACT_APP_API_URL?.trim();
//   if (envUrl) return envUrl;
//   try {
//     const origin = window.location.origin;
//     const u = new URL(origin);
//     const p = u.port || '';
//     if (p === '3001' || p === '3010') return 'http://localhost:3000/api';
//     return `${origin}/api`;
//   } catch {
//     return 'http://localhost:3000/api';
//   }
// })();

const API_BASE_URL = (() => {
  const envUrl = process.env.REACT_APP_API_URL?.trim();
  if (envUrl) return envUrl.replace(/\/+$/, '');
  return '/api';
})();


class ApiService {
    private axiosInstance: AxiosInstance;

    constructor() {
        this.axiosInstance = axios.create({
            baseURL: API_BASE_URL,
            timeout: 30000,
            headers: {
                'Content-Type': 'application/json',
            },
        });

        // Request interceptor to add auth token
        this.axiosInstance.interceptors.request.use(
            (config) => {
                const token = localStorage.getItem('token');
                if (token) {
                    config.headers.Authorization = `Bearer ${token}`;
                }
                const selectedCompanyId = localStorage.getItem('selectedCompanyId');
                const isGet = String(config.method || 'get').toLowerCase() === 'get';
                const eligiblePath = (config.url || '').startsWith('/sap/') || (config.url || '').startsWith('/item-master') || (config.url || '').startsWith('/schedules') || (config.url || '').startsWith('/mcsr');
                if (selectedCompanyId && isGet && eligiblePath) {
                    config.params = config.params || {};
                    if (config.params && typeof config.params === 'object' && !('companyId' in (config.params as any))) {
                        (config.params as any).companyId = Number(selectedCompanyId);
                    }
                }
                return config;
            },
            (error) => {
                return Promise.reject(error);
            }
        );

        // Response interceptor for error handling
        this.axiosInstance.interceptors.response.use(
            (response) => response,
            (error: AxiosError) => {
                const requestUrl = String(error.config?.url || '');
                // The sign-in requests answer 401 for a wrong password, not for an expired session.
                const isLoginRequest = requestUrl.includes('/auth/login') || requestUrl.includes('/auth/first-time-password-change');
                if (error.response?.status === 401 && !isLoginRequest) {
                    // Unauthorized - redirect to login
                    localStorage.removeItem('token');
                    window.location.href = '/login';
                }
                return Promise.reject(error);
            }
        );
    }

    // Generic methods
    async get<T>(url: string, params?: any, options?: { timeout?: number }): Promise<T> {
        const response = await this.axiosInstance.get<T>(url, { params, ...options });
        return response.data;
    }

    async post<T>(url: string, data?: any, options?: { timeout?: number }): Promise<T> {
        const response = await this.axiosInstance.post<T>(url, data, options);
        return response.data;
    }

    async put<T>(url: string, data?: any): Promise<T> {
        const response = await this.axiosInstance.put<T>(url, data);
        return response.data;
    }

    async patch<T>(url: string, data?: any): Promise<T> {
        const response = await this.axiosInstance.patch<T>(url, data);
        return response.data;
    }

    async delete<T>(url: string): Promise<T> {
        const response = await this.axiosInstance.delete<T>(url);
        return response.data;
    }

    /**
     * Render a self-contained HTML document to a PDF buffer server-side.
     * Returns a Blob suitable for triggering a browser download. Replaces the
     * legacy `window.open()` + `printWindow.print()` flow which is unreliable
     * on mobile browsers due to popup blockers and inconsistent PDF output.
     *
     * The HTML payload is sent as-is and rendered with Puppeteer at A4
     * portrait (8mm/10mm margins) by default — matching the @page rule used
     * in PaymentCertificate's print template.
     */
    async renderPdf(
        html: string,
        opts: { filename?: string; format?: 'A4' | 'Letter' | 'Legal'; landscape?: boolean } = {},
    ): Promise<Blob> {
        const response = await this.axiosInstance.post('/pdf/render', {
            html,
            filename: opts.filename,
            format: opts.format,
            landscape: opts.landscape,
        }, {
            responseType: 'blob',
        });
        return response.data as Blob;
    }

    // Auth methods
    async login(username: string, password: string): Promise<any> {
        return this.post('/auth/login', { username, password });
    }

    async register(userData: any): Promise<any> {
        return this.post('/auth/register', userData);
    }

    async firstTimePasswordChange(userId: number, currentPassword: string, newPassword: string): Promise<any> {
        return this.post('/auth/first-time-password-change', { userId, currentPassword, newPassword });
    }

    /** Public: the other applications on this server, for the login page's picker (none on prod and KSA). */
    async getServerApps(): Promise<{ success?: boolean; data?: { apps?: unknown } }> {
        return this.get('/auth/server-apps', undefined, { timeout: 10000 });
    }

    async getProfile(): Promise<any> {
        return this.get('/auth/profile');
    }

    // PR methods
    async createPR(prData: any): Promise<any> {
        const selectedCompanyId = Number(localStorage.getItem('selectedCompanyId') || '');
        const final = { ...(prData || {}) } as any;
        if (!final.companyid && selectedCompanyId) final.companyid = selectedCompanyId;
        return this.post('/pr', final);
    }

    async createPRDraft(prData: any): Promise<any> {
        const selectedCompanyId = Number(localStorage.getItem('selectedCompanyId') || '');
        const final = { ...(prData || {}) } as any;
        if (!final.companyid && selectedCompanyId) final.companyid = selectedCompanyId;
        return this.post('/pr/draft', final);
    }

    async updatePRDraft(prId: number, prData: any): Promise<any> {
        const selectedCompanyId = Number(localStorage.getItem('selectedCompanyId') || '');
        const final = { ...(prData || {}) } as any;
        if (!final.companyid && selectedCompanyId) final.companyid = selectedCompanyId;
        return this.put(`/pr/draft/${prId}`, final);
    }

    async discardPRDraft(prId: number): Promise<any> {
        return this.delete(`/pr/draft/${prId}`);
    }

    async getPR(id: number): Promise<any> {
        return this.get(`/pr/${id}`);
    }

    async getMyPRs(): Promise<any> {
        const selectedCompanyId = Number(localStorage.getItem('selectedCompanyId') || '');
        const params = selectedCompanyId ? { companyId: selectedCompanyId } : {};
        return this.get('/pr/my-prs', params);
    }

    async getMyDrafts(): Promise<any> {
        const selectedCompanyId = Number(localStorage.getItem('selectedCompanyId') || '');
        const params = selectedCompanyId ? { companyId: selectedCompanyId } : {};
        return this.get('/pr/my-drafts', params);
    }

    async getApprovalTiming(params?: { from?: string; to?: string }): Promise<any> {
        const selectedCompanyId = Number(localStorage.getItem('selectedCompanyId') || '');
        return this.get('/pr/approval-timing', {
            ...(params || {}), ...(selectedCompanyId ? { companyId: selectedCompanyId } : {}),
        });
    }

  async getPendingApprovals(): Promise<any> {
    const selectedCompanyId = Number(localStorage.getItem('selectedCompanyId') || '');
    const params = selectedCompanyId ? { companyId: selectedCompanyId } : {};
    return this.get('/pr/pending-approvals', params);
  }

  async getApproverPRs(): Promise<any> {
    const selectedCompanyId = Number(localStorage.getItem('selectedCompanyId') || '');
    const params = selectedCompanyId ? { companyId: selectedCompanyId } : {};
    return this.get('/pr/approver-tracking', params);
  }

  async getApprovalActivityStats(): Promise<any> {
    const selectedCompanyId = Number(localStorage.getItem('selectedCompanyId') || '');
    const params = selectedCompanyId ? { companyId: selectedCompanyId } : {};
    return this.get('/pr/my-approval-activity', params);
  }

  async createPOFromPR(prId: number): Promise<any> {
    return this.post(`/pr/${prId}/create-po`);
  }

  async submitPR(prId: number): Promise<any> {
    return this.post(`/pr/${prId}/submit`);
  }

  async reinitiatePR(prId: number, reason: string): Promise<any> {
    return this.post(`/pr/${prId}/reinitiate`, { reason });
  }

  async getPRDetailReport(params: Record<string, string | number | undefined>): Promise<any> {
    return this.get('/reports/pr-details', params);
  }

  async movePRToCompleted(prId: number): Promise<any> {
    return this.post(`/pr/${prId}/move-to-completed`);
  }

  async getPRPostingAttempts(prId: number): Promise<any> {
    return this.get(`/pr/${prId}/posting-attempts`);
  }

  /** The company's PR rules (project on every cost center line, Service unit price 1). */
  async getPRCompanyRules(companyId: number): Promise<any> {
    return this.get('/pr/company-rules', { companyid: companyId });
  }

  /** Admin: what SAP holds for a PR whose posting outcome is unknown. */
  async getPRPostingReview(prId: number): Promise<any> {
    return this.get(`/pr/${prId}/posting-review`);
  }

  /** Admin: link the SAP document (outcome 'posted') or confirm none exists ('not-posted'). */
  async resolvePRPostingReview(prId: number, body: {
    outcome: 'posted' | 'not-posted';
    note: string;
    docEntry?: number;
    docNum?: number;
    confirmNoDocument?: boolean;
  }): Promise<any> {
    return this.post(`/pr/${prId}/posting-review`, body);
  }

  subscribePRStatus(prId: number): EventSource {
    const token = localStorage.getItem('token') || '';
    const q = token ? `?token=${encodeURIComponent(token)}` : '';
    const url = `${API_BASE_URL}/pr/${prId}/subscribe${q}`;
    const es = new EventSource(url);
    return es;
  }

  // File attachments methods
  async uploadPRAttachments(prId: number, files: File[], onUploadProgress?: (e: any) => void): Promise<any> {
    const form = new FormData();
    for (const f of files) form.append('files', f);
    const response = await this.axiosInstance.post(`/files/upload/${prId}`, form, {
      headers: { 'Content-Type': 'multipart/form-data' },
      onUploadProgress,
    });
    return response.data;
  }

  async uploadSingleAttachment(prId: number, file: File, onUploadProgress?: (e: any) => void): Promise<any> {
    const form = new FormData();
    form.append('files', file);
    const response = await this.axiosInstance.post(`/files/upload/${prId}`, form, {
      headers: { 'Content-Type': 'multipart/form-data' },
      onUploadProgress,
    });
    return response.data;
  }

  async getPRAttachments(prId: number): Promise<any> {
    return this.get(`/files/pr/${prId}`);
  }

  async deleteAttachment(attachmentId: number): Promise<any> {
    return this.delete(`/files/${attachmentId}`);
  }

  getAttachmentDownloadUrl(attachmentId: number): string {
    const token = localStorage.getItem('token') || '';
    const q = token ? `?token=${encodeURIComponent(token)}` : '';
    return `${API_BASE_URL}/files/download/${attachmentId}${q}`;
  }

  getAttachmentViewUrl(attachmentId: number): string {
    const token = localStorage.getItem('token') || '';
    const q = token ? `?token=${encodeURIComponent(token)}` : '';
    return `${API_BASE_URL}/files/view/${attachmentId}${q}`;
  }

  async fetchAttachmentBlob(attachmentId: number): Promise<{ blob: Blob; contentType: string }> {
    const response = await this.axiosInstance.get(`/files/download/${attachmentId}`, { responseType: 'blob' });
    const ct = String((response.headers as any)['content-type'] || 'application/octet-stream');
    return { blob: response.data as Blob, contentType: ct };
  }

    // Approval methods
    async processApproval(prId: number, action: string, comments?: string): Promise<any> {
        return this.post(`/approval/${prId}/process`, { action, comments });
    }

    async getWorkflowStatus(prId: number): Promise<any> {
        return this.get(`/approval/${prId}/workflow`);
    }

    async getApprovalHistory(prId: number): Promise<any> {
        return this.get(`/approval/${prId}/history`);
    }

  async resolveApprovalToken(token: string): Promise<any> {
    return this.get(`/approval/token/${encodeURIComponent(token)}`);
  }

    // Approval workflow settings
    async listApprovalWorkflows(params?: { documentType?: 'PR' | 'PO'; requisitionType?: string; allocationType?: string; companyId?: number; flowName?: string }): Promise<any> {
        const selectedCompanyId = Number(localStorage.getItem('selectedCompanyId') || '');
        const final = { ...(params || {}) } as any;
        if (!final.documentType) final.documentType = 'PR';
        if (!final.companyId && selectedCompanyId) final.companyId = selectedCompanyId;
        return this.get('/user-management/approval-workflows', final);
    }

    async getApprovalWorkflowPendingCounts(params?: { documentType?: 'PR' | 'PO'; requisitionType?: string; allocationType?: string; companyId?: number; flowName?: string }): Promise<any> {
        const selectedCompanyId = Number(localStorage.getItem('selectedCompanyId') || '');
        const final = { ...(params || {}) } as any;
        if (!final.documentType) final.documentType = 'PR';
        if (!final.companyId && selectedCompanyId) final.companyId = selectedCompanyId;
        return this.get('/user-management/approval-workflows/pending-counts', final);
    }

    async createApprovalWorkflow(data: any): Promise<any> {
        const selectedCompanyId = Number(localStorage.getItem('selectedCompanyId') || '');
        const final = { ...(data || {}) } as any;
        if (!final.companyid && selectedCompanyId) final.companyid = selectedCompanyId;
        return this.post('/user-management/approval-workflows', final);
    }

    async saveApprovalWorkflowGroup(data: any): Promise<any> {
        const selectedCompanyId = Number(localStorage.getItem('selectedCompanyId') || '');
        const final = { ...(data || {}) } as any;
        if (!final.documenttype) final.documenttype = 'PR';
        if (final.companyid === undefined && selectedCompanyId) final.companyid = selectedCompanyId;
        if (final.original && final.original.companyid === undefined && selectedCompanyId) {
            final.original.companyid = selectedCompanyId;
        }
        return this.post('/user-management/approval-workflows/save-group', final);
    }

    async updateApprovalWorkflow(id: number, data: any): Promise<any> {
        return this.put(`/user-management/approval-workflows/${id}`, data);
    }

    async deleteApprovalWorkflow(id: number): Promise<any> {
        return this.delete(`/user-management/approval-workflows/${id}`);
    }

    async reorderApprovalWorkflows(payload: { documenttype?: 'PR' | 'PO'; requisitiontype: string; allocationtype: string; orderedIds: number[]; flowname?: string; companyid?: number }): Promise<any> {
        const selectedCompanyId = Number(localStorage.getItem('selectedCompanyId') || '');
        const final = { ...(payload || {}) } as any;
        if (!final.documenttype) final.documenttype = 'PR';
        if (!final.companyid && selectedCompanyId) final.companyid = selectedCompanyId;
        return this.post('/user-management/approval-workflows/reorder', final);
    }

    async deleteApprovalWorkflowGroup(payload: { documenttype?: 'PR' | 'PO'; requisitiontype: string; allocationtype: string; flowname: string; companyid?: number | null; facilitytype?: string | null; cycle?: string | null; allocationcode?: string | null }): Promise<any> {
        const selectedCompanyId = Number(localStorage.getItem('selectedCompanyId') || '');
        const final = { ...(payload || {}) } as any;
        if (!final.documenttype) final.documenttype = 'PR';
        if (final.companyid === undefined && selectedCompanyId) final.companyid = selectedCompanyId;
        return this.post('/user-management/approval-workflows/delete-group', final);
    }

    // Bulk workflow management operations
    async bulkAssignUsersToWorkflows(payload: {
        workflowIds: number[];
        userIds: number[];
        role: 'initiator' | 'approver';
        stageNumber?: number;
    }): Promise<any> {
        return this.post('/user-management/approval-workflows/bulk-assign-users', payload);
    }

    async bulkRemoveUsersFromWorkflows(payload: {
        workflowIds: number[];
        userIds: number[];
        role?: 'initiator' | 'approver';
    }): Promise<any> {
        return this.post('/user-management/approval-workflows/bulk-remove-users', payload);
    }

    async bulkReassignUsers(payload: {
        workflowIds: number[];
        fromUserIds: number[];
        toUserIds: number[];
        preserveRoles?: boolean;
    }): Promise<any> {
        return this.post('/user-management/approval-workflows/bulk-reassign-users', payload);
    }

    async bulkModifyWorkflowStage(payload: {
        workflowIds: number[];
        stageNumber: number;
        updates: {
            rule?: 'ANY' | 'ALL';
            addUsers?: number[];
            removeUsers?: number[];
            replaceUsers?: number[];
        };
    }): Promise<any> {
        return this.put('/user-management/approval-workflows/bulk-modify-stage', payload);
    }

    async getWorkflowsByProject(projectCode: string): Promise<any> {
        const selectedCompanyId = Number(localStorage.getItem('selectedCompanyId') || '') || undefined;
        return this.get('/user-management/approval-workflows/by-project', { projectCode, companyId: selectedCompanyId });
    }

    async getUserWorkflows(userId: number): Promise<any> {
        return this.get(`/user-management/users/${userId}/workflows`);
    }

    async getWorkflowStats(): Promise<any> {
        const selectedCompanyId = Number(localStorage.getItem('selectedCompanyId') || '') || undefined;
        return this.get('/user-management/approval-workflows/stats', { companyId: selectedCompanyId });
    }


    // Approval initiators
    async setApprovalInitiators(payload: { documenttype?: 'PR' | 'PO'; requisitiontype: string; allocationtype: string; userids: number[]; flowname?: string; companyid?: number }): Promise<any> {
        const selectedCompanyId = Number(localStorage.getItem('selectedCompanyId') || '');
        const final = { ...(payload || {}) } as any;
        if (!final.documenttype) final.documenttype = 'PR';
        if (!final.companyid && selectedCompanyId) final.companyid = selectedCompanyId;
        return this.post('/user-management/approval-initiators', final);
    }

    async listApprovalInitiators(params: { documenttype?: 'PR' | 'PO'; requisitiontype?: string; allocationtype?: string; flowname?: string; companyid?: number; facilitytype?: string; cycle?: string; allocationcode?: string; exact?: boolean } = {}): Promise<any> {
        const selectedCompanyId = Number(localStorage.getItem('selectedCompanyId') || '');
        const final = { ...(params || {}) } as any;
        if (!final.documenttype) final.documenttype = 'PR';
        if (!final.companyid && selectedCompanyId) final.companyid = selectedCompanyId;
        return this.get('/user-management/approval-initiators', final);
    }

    async getUserAvailableFlowOptions(userId: number): Promise<any> {
        const selectedCompanyId = Number(localStorage.getItem('selectedCompanyId') || '');
        const params: any = { userId };
        if (selectedCompanyId) params.companyId = selectedCompanyId;
        return this.get('/user-management/approval-flow-options', params);
    }

    // Budget methods
    async checkBudget(budgetData: any): Promise<any> {
        return this.post('/budget/check', budgetData);
    }

  async getBudgetUtilization(filters?: any): Promise<any> {
      return this.get('/budget/utilization', filters);
  }

  async createBudget(payload: { fiscalyear: number; allocationtype: string; allocationcode: string; accountcode: string; budgetamount: number; startdate: string | Date; enddate: string | Date }): Promise<any> {
    return this.post('/budget', payload);
  }

  async listClassifications(activeOnly?: boolean): Promise<any> {
    const params: any = {};
    if (activeOnly) params.activeOnly = true;
    return this.get('/budget/hierarchy/classifications', params);
  }

  async createClassification(payload: { code: string; name: string }): Promise<any> {
    return this.post('/budget/hierarchy/classifications', payload);
  }

  async listCostGroups(classificationId?: number, activeOnly?: boolean): Promise<any> {
    const params: any = {};
    if (typeof classificationId === 'number') params.classificationId = classificationId;
    if (activeOnly) params.activeOnly = true;
    return this.get('/budget/hierarchy/cost-groups', params);
  }

  async createCostGroup(payload: { classificationid: number; code: string; name: string }): Promise<any> {
    return this.post('/budget/hierarchy/cost-groups', payload);
  }

  async listProjectCostGroups(costGroupId?: number, activeOnly?: boolean): Promise<any> {
    const params: any = {};
    if (typeof costGroupId === 'number') params.costGroupId = costGroupId;
    if (activeOnly) params.activeOnly = true;
    return this.get('/budget/hierarchy/project-cost-groups', params);
  }

  async createProjectCostGroup(payload: { costgroupid: number; code: string; name: string }): Promise<any> {
    return this.post('/budget/hierarchy/project-cost-groups', payload);
  }

  async listProjectBudgetLines(projectCode: string, year?: number): Promise<any> {
    const params: any = { projectCode };
    if (typeof year === 'number') params.year = year;
    return this.get('/budget/project-lines', params);
  }

  async upsertProjectBudgetLine(payload: { projectcode: string; year: number; classificationid: number; costgroupid: number; projectcostgroupid: number; originalbudget: number; internalchanges?: number; clientchanges?: number; note?: string }): Promise<any> {
    return this.post('/budget/project-lines/upsert', payload);
  }

  async getProjectBudgetHeader(projectCode: string): Promise<any> {
    const params: any = { projectCode };
    return this.get('/budget/project-header', params);
  }

  async upsertProjectBudgetHeader(payload: { projectcode: string; startdate?: string | Date | null; enddate?: string | Date | null; contractnumber?: string | null; totalvalue?: number | null }): Promise<any> {
    return this.post('/budget/project-header/upsert', payload);
  }

  async getBudgetsByAllocation(allocationType: string, allocationCode: string, fiscalYear?: number): Promise<any> {
    const params: any = {};
    if (fiscalYear) params.fiscalYear = fiscalYear;
    return this.get(`/budget/${encodeURIComponent(allocationType)}/${encodeURIComponent(allocationCode)}`, params);
  }

    async getLogReports(params?: { store?: 'db'|'file'; reportType?: 'application'|'subcontracting'; source?: string; date?: string; level?: string; transactionType?: string; status?: string; limit?: number; search?: string }): Promise<any> {
        return this.get('/reports/logs', params);
    }

    async getApprovalTracking(params?: {
        date?: string;
        startDate?: string;
        endDate?: string;
        prNumber?: string;
        requesterUserId?: number;
        status?: string;
        companyId?: number;
    }): Promise<any> {
        return this.get('/reports/approval-tracking', params);
    }

    async exportApprovalTracking(params?: {
        date?: string;
        startDate?: string;
        endDate?: string;
        prNumber?: string;
        requesterUserId?: number;
        status?: string;
        companyId?: number;
    }): Promise<void> {
        const queryParams = new URLSearchParams();
        if (params?.date) queryParams.append('date', params.date);
        if (params?.startDate) queryParams.append('startDate', params.startDate);
        if (params?.endDate) queryParams.append('endDate', params.endDate);
        if (params?.prNumber) queryParams.append('prNumber', params.prNumber);
        if (params?.requesterUserId) queryParams.append('requesterUserId', String(params.requesterUserId));
        if (params?.status) queryParams.append('status', params.status);
        if (params?.companyId) queryParams.append('companyId', String(params.companyId));

        const token = localStorage.getItem('token');
        const url = `${this.axiosInstance.defaults.baseURL}/reports/approval-tracking/export?${queryParams.toString()}`;

        const response = await fetch(url, {
            headers: {
                'Authorization': `Bearer ${token}`
            }
        });

        if (!response.ok) throw new Error('Export failed');

        const blob = await response.blob();
        const downloadUrl = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = downloadUrl;
        link.download = `approval-tracking-${new Date().toISOString().slice(0, 10)}.xlsx`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(downloadUrl);
    }

    // SAP B1 methods
  async getItems(): Promise<any> {
    return this.get('/sap/items');
  }

    async getServiceItems(): Promise<any> {
        return this.get('/sap/service-items');
    }

  async getProjects(): Promise<any> {
    return this.get('/sap/projects');
  }

  async getDistributionRulesByLevel(level?: number): Promise<any> {
    const params: any = {};
    if (typeof level === 'number') params.level = level;
    return this.get('/sap/distribution-rules', params);
  }

  async listProjects(companyId?: number): Promise<any> {
    const params = companyId ? { companyId } : undefined;
    return this.get('/projects', params);
  }

  async createProject(payload: { companyid?: number | null; code: string; name?: string; active?: boolean }): Promise<any> {
    return this.post('/projects', payload);
  }

  async updateProject(id: number, payload: { code?: string; name?: string; active?: boolean; data?: any }): Promise<any> {
    return this.put(`/projects/${id}`, payload);
  }

  async deleteProject(id: number): Promise<any> {
    return this.delete(`/projects/${id}`);
  }

  async getProjectByCode(code: string, companyId?: number): Promise<any> {
    const params: any = { code };
    if (companyId) params.companyId = companyId;
    return this.get('/projects/by-code', params);
  }

    async getAccounts(params?: { companyId?: number; q?: string; limit?: number }): Promise<any> {
        return this.get('/sap/accounts', params);
    }

    async getChartOfAccounts(params?: { companyId?: number; q?: string; limit?: number }): Promise<any> {
        return this.get('/sap/chart-of-accounts', params);
    }

  async getVendors(): Promise<any> {
      return this.get('/sap/vendors');
  }

  async getWarehouses(): Promise<any> {
      return this.get('/sap/warehouses');
  }

  async getItemGroups(params?: { companyId?: number; code?: string }): Promise<any> {
    return this.get('/sap/item-groups', params);
  }

    // User Management methods (Admin-only for most)
    async getUsers(params?: { role?: string; active?: boolean }): Promise<any> {
        return this.get('/user-management/users', params);
    }

    async getAvailableUserRoles(): Promise<any> {
        return this.get('/user-management/users/roles/available');
    }

    async getUserById(id: number): Promise<any> {
        return this.get(`/user-management/users/${id}`);
    }

    /** Roles a user holds on top of the main role (e.g. the item request approval roles). */
    async getUserAdditionalRoles(id: number): Promise<any> {
        return this.get(`/user-management/users/${id}/additional-roles`);
    }

    async setUserAdditionalRoles(id: number, roles: string[]): Promise<any> {
        return this.put(`/user-management/users/${id}/additional-roles`, { roles });
    }

    async updateUser(id: number, data: {
        email?: string;
        fullname?: string;
        department?: string;
        role?: string;
        isactive?: boolean;
        sapusercode?: string;
    }): Promise<any> {
        return this.put(`/user-management/users/${id}`, data);
    }

    async deleteUser(id: number): Promise<any> {
        return this.delete(`/user-management/users/${id}`);
    }

    async deactivateUser(id: number): Promise<any> {
        return this.post(`/user-management/users/${id}/deactivate`, {});
    }

    async resetUserPassword(id: number, newPassword: string): Promise<any> {
        return this.post(`/user-management/users/${id}/reset-password`, { newPassword });
    }

    // Company Management methods
    async getCompanies(active?: boolean): Promise<any> {
        const params: any = {};
        if (active !== undefined) params.active = active;
        return this.get('/company-management/companies', params);
    }

    async getMyCompanies(active?: boolean): Promise<any> {
        const params: any = {};
        if (active !== undefined) params.active = active;
        return this.get('/company-management/my-companies', params);
    }

    async createCompany(payload: { companycode: string; companyname: string; databasename: string; isactive?: boolean }): Promise<any> {
        return this.post('/company-management/companies', payload);
    }

    async updateCompany(id: number, payload: { companycode?: string; companyname?: string; databasename?: string; isactive?: boolean }): Promise<any> {
        return this.put(`/company-management/companies/${id}`, payload);
    }

    async deleteCompany(id: number): Promise<any> {
        return this.delete(`/company-management/companies/${id}`);
    }

    /** The company's SAP additional expenses (Freight setup), read live: what PO charges can be sent to SAP as. */
    async getCompanySapAdditionalExpenses(id: number): Promise<any> {
        return this.get(`/company-management/companies/${id}/sap-additional-expenses`);
    }

    async getUserCompanies(userId: number): Promise<any> {
        return this.get(`/company-management/users/${userId}/companies`);
    }

  async addUserCompany(userId: number, payload: { companyid: number; isprimary?: boolean }): Promise<any> {
    return this.post(`/company-management/users/${userId}/companies`, payload);
  }

  async removeUserCompany(userId: number, companyId: number): Promise<any> {
    return this.delete(`/company-management/users/${userId}/companies/${companyId}`);
  }

  /**
   * A user's access per company: { allCompanies, companies: [{ companyId, companyName, databaseName,
   * isPrimary, access: { projects, divisions, departments }, review }], review }; each kind is
   * { all, codes } (utils/userAccess).
   */
  async getUserAccess(userId: number): Promise<any> {
    return this.get(`/company-management/users/${userId}/access`);
  }

  /** Saves one company's projects, divisions and departments of a user (each { all } or { codes }). */
  async setUserCompanyAccess(userId: number, companyId: number, access: Record<'projects' | 'divisions' | 'departments', { all: boolean; codes: string[] }>): Promise<any> {
    return this.put(`/company-management/users/${userId}/access/${companyId}`, access);
  }

  /** "Mark reviewed": removes the notes the move to per-company access left for this user. */
  async clearUserAccessReview(userId: number): Promise<any> {
    return this.delete(`/company-management/users/${userId}/access-review`);
  }

  /** A company's projects (with their division), divisions and departments for the Access dialog. */
  async getCompanyAccessLists(companyId: number): Promise<any> {
    return this.get('/company-management/access-lists', { companyId });
  }

  /** "All companies": every active company, including companies added later. */
  async setUserAllCompanies(userId: number, all: boolean): Promise<any> {
    return this.put(`/company-management/users/${userId}/all-companies`, { all });
  }

    async grantAllUserCompanies(userId: number): Promise<any> {
        return this.post(`/company-management/users/${userId}/companies/grant-all`);
    }

    // Access Management: Per-user modules
    async getUserModules(userId: number): Promise<any> {
        return this.get(`/access-management/users/${userId}/modules`);
    }

    async addUserModule(userId: number, modulekey: string): Promise<any> {
        return this.post(`/access-management/users/${userId}/modules`, { modulekey });
    }

    async removeUserModule(userId: number, moduleKey: string): Promise<any> {
        return this.delete(`/access-management/users/${userId}/modules/${moduleKey}`);
    }

    async grantAllUserModules(userId: number): Promise<any> {
        return this.post(`/access-management/users/${userId}/modules/grant-all`);
    }

    // Schedule Jobs methods
    async initSchedules(): Promise<any> {
        return this.post('/schedules/init');
    }

    async getScheduleEndpoints(): Promise<any> {
        return this.get('/schedules/endpoints');
    }

    async getScheduleFields(endpointKey: string, companyId?: number): Promise<any> {
        const params = companyId ? { companyId } : undefined;
        return this.get(`/schedules/endpoints/${endpointKey}/fields`, params);
    }

    async listSchedules(): Promise<any> {
        const selectedCompanyId = Number(localStorage.getItem('selectedCompanyId') || '');
        const params = selectedCompanyId ? { companyId: selectedCompanyId } : {};
        return this.get('/schedules', params);
    }

    /** The jobs of one company (Schedule Jobs › Copy to company), whatever company the top bar has selected. */
    async listSchedulesForCompany(companyId: number): Promise<any> {
        return this.get('/schedules', { companyId });
    }

    /** Copies jobs to another company; answers { created, skipped }. */
    async copySchedules(payload: {
        fromCompanyId: number;
        toCompanyId: number;
        scheduleIds: number[];
        startPaused: boolean;
    }): Promise<any> {
        return this.post('/schedules/copy', payload);
    }

    async createSchedule(payload: {
        name: string;
        companyid?: number | null;
        endpointkey: string;
        selectedfields: string[];
        condition?: string;
        datefield?: string;
        cronexpression: string;
        backfillchunkdays?: number | null;
        backfillstartdate?: string | null;
    }): Promise<any> {
        return this.post('/schedules', payload);
    }

    async updateSchedule(id: number, payload: any): Promise<any> {
        return this.put(`/schedules/${id}`, payload);
    }

  async runSchedule(id: number): Promise<any> {
    // Use longer timeout for schedule runs (5 minutes) to handle large datasets.
    // Unwrap to `response.data` like every other method here — returning the raw
    // AxiosResponse made callers read `res.success` off the envelope (undefined),
    // so a successful run was reported to the user as "Run failed".
    const response = await this.axiosInstance.post(`/schedules/${id}/run`, {}, { timeout: 300000 });
    return response.data;
  }

  async deleteSchedule(id: number): Promise<any> {
    return this.delete(`/schedules/${id}`);
  }

    async getScheduleRuns(id: number): Promise<any> {
        return this.get(`/schedules/${id}/runs`);
    }

    async getItemsCustom(companyId?: number, fields: string[] = [], filter?: string): Promise<any> {
        const params: any = {};
        if (companyId) params.companyId = companyId;
        if (fields && fields.length) params.fields = fields.join(',');
        if (filter) params.filter = filter;
        return this.get('/sap/items/custom', params);
    }

    async listLocalItemMaster(companyId?: number, fields?: string[], requisitionType?: string): Promise<any> {
        const params: any = {};
        if (companyId) params.companyId = companyId;
        if (fields && fields.length) params.fields = fields.join(',');
        if (requisitionType) params.requisitionType = requisitionType;
        return this.get('/item-master', params);
    }

    /**
     * Each item's Auxiliary (SAP item field U_AUX_Code) for PR / PO lines: { [itemCode]: code | null }.
     * An item the server could not find out about (SAP did not answer) is left out. At most 200 codes.
     */
    async getItemAuxiliaries(codes: string[], companyId?: number): Promise<Record<string, string | null>> {
        const params: any = { codes: codes.join(',') };
        if (companyId) params.companyId = companyId;
        const res: any = await this.get('/item-master/auxiliary', params);
        return (res && res.data) || {};
    }

    async syncLocalItemMaster(scheduleId: number): Promise<any> {
        return this.post('/item-master/sync', { scheduleId });
    }

    async updateLocalItem(id: number, data: Record<string, any>): Promise<any> {
        return this.put(`/item-master/${id}`, { data });
    }

    async deleteLocalItem(id: number): Promise<any> {
        return this.delete(`/item-master/${id}`);
    }

    async createLocalItem(companyid: number | undefined, itemcode: string, data: Record<string, any>): Promise<any> {
        return this.post('/item-master', { companyid, itemcode, data });
    }

    async exportPRWorkflows(): Promise<void> {
        const token = localStorage.getItem('token');
        const url = `${this.axiosInstance.defaults.baseURL}/user-management/pr-workflows/export`;

        const response = await fetch(url, {
            headers: {
                'Authorization': `Bearer ${token}`
            }
        });

        if (!response.ok) throw new Error('Export failed');

        const blob = await response.blob();
        const downloadUrl = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = downloadUrl;
        link.download = `PR_Workflows_Export_${new Date().toISOString().slice(0, 10)}.xlsx`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(downloadUrl);
    }

    // Role Management APIs
    async getRoles(includeInactive: boolean = false): Promise<any> {
        return this.get('/roles', { includeInactive });
    }

    async getRoleById(roleId: number): Promise<any> {
        return this.get(`/roles/${roleId}`);
    }

    async getAvailablePermissions(): Promise<any> {
        return this.get('/roles/permissions');
    }

    async getMyPermissions(): Promise<any> {
        return this.get('/roles/my-permissions');
    }

    async createRole(roleData: any): Promise<any> {
        return this.post('/roles', roleData);
    }

    async updateRole(roleId: number, roleData: any): Promise<any> {
        return this.put(`/roles/${roleId}`, roleData);
    }

    async deleteRole(roleId: number): Promise<any> {
        return this.delete(`/roles/${roleId}`);
    }

    // Cost Center Mapping APIs
    async getCostCenterMappings(companyId?: number): Promise<any> {
        const params: any = {};
        if (companyId) params.companyId = companyId;
        return this.get('/cost-center-mappings', params);
    }

    async getDistributionRules(companyId?: number, dimension?: number): Promise<any> {
        const params: any = {};
        if (companyId) params.companyId = companyId;
        if (dimension) params.dimension = dimension;
        return this.get('/cost-center-mappings/distribution-rules', params);
    }

    async saveCostCenterMapping(mappingData: any): Promise<any> {
        return this.post('/cost-center-mappings', mappingData);
    }

    async batchUpdateCostCenterMappings(mappings: any[], companyId?: number): Promise<any> {
        return this.post('/cost-center-mappings/batch', { mappings, companyId });
    }

    async deleteCostCenterMapping(mappingId: number): Promise<any> {
        return this.delete(`/cost-center-mappings/${mappingId}`);
    }

    // Transaction Sync APIs
    async getTransactionSyncs(companyId?: number): Promise<any> {
        const params: any = {};
        if (companyId) params.companyId = companyId;
        return this.get('/transaction-sync', params);
    }

    async getTransactionSyncById(syncId: number): Promise<any> {
        return this.get(`/transaction-sync/${syncId}`);
    }

    async getAvailableEndpoints(): Promise<any> {
        return this.get('/transaction-sync/endpoints');
    }

    async getEndpointFields(endpoint: string): Promise<any> {
        return this.get(`/transaction-sync/endpoints/${endpoint}/fields`);
    }

    async createTransactionSync(syncData: any): Promise<any> {
        return this.post('/transaction-sync', syncData);
    }

    async updateTransactionSync(syncId: number, syncData: any): Promise<any> {
        return this.put(`/transaction-sync/${syncId}`, syncData);
    }

    async deleteTransactionSync(syncId: number): Promise<any> {
        return this.delete(`/transaction-sync/${syncId}`);
    }

    async executeTransactionSync(syncId: number): Promise<any> {
        const response = await this.axiosInstance.post(`/transaction-sync/${syncId}/execute`, {}, {
            timeout: 300000 // 5 minutes timeout for sync execution
        });
        return response.data;
    }

    async getTransactionSyncLogs(syncId: number, limit?: number): Promise<any> {
        const params: any = {};
        if (limit) params.limit = limit;
        return this.get(`/transaction-sync/${syncId}/logs`, params);
    }

    async getSystemConfig(): Promise<any> {
        return this.get('/settings/system-config');
    }

    async updateSystemConfig(updates: Array<{ key: string; value: string }>): Promise<any> {
        return this.put('/settings/system-config', { updates });
    }

    // Monthly Budget APIs
    async getMonthlyBudgets(params?: any): Promise<any> {
        return this.get('/monthly-budget', params);
    }

    async getMonthlyBudgetById(id: number): Promise<any> {
        return this.get(`/monthly-budget/${id}`);
    }

    async createMonthlyBudget(data: any): Promise<any> {
        return this.post('/monthly-budget', data);
    }

    async updateMonthlyBudget(id: number, data: any): Promise<any> {
        return this.put(`/monthly-budget/${id}`, data);
    }

    async deleteMonthlyBudget(id: number): Promise<any> {
        return this.delete(`/monthly-budget/${id}`);
    }

    async bulkUpsertMonthlyBudgets(budgets: any[]): Promise<any> {
        return this.post('/monthly-budget/bulk/upsert', { budgets });
    }

    async bulkDeleteMonthlyBudgets(ids: number[]): Promise<any> {
        return this.post('/monthly-budget/bulk/delete', { ids });
    }

    async checkLineBudget(data: {
        projectCode: string;
        accountCode?: string;
        fiscalYear: number;
        month: number;
        amount: number;
        currentPRID?: number;
    }): Promise<any> {
        return this.post('/budget-check/check-line', data);
    }

    async checkPRBudget(data: {
        projectCode: string;
        lines: any[];
        fiscalYear: number;
        month: number;
    }): Promise<any> {
        return this.post('/budget-check/check-pr', data);
    }

    async getBudgetProjects(params?: any): Promise<any> {
        return this.get('/monthly-budget/data/projects', params);
    }

    async getBudgetDepartments(params?: any): Promise<any> {
        return this.get('/monthly-budget/data/departments', params);
    }

    async getBudgetCostCodes(params?: any): Promise<any> {
        return this.get('/monthly-budget/data/costcodes', params);
    }

    // PO methods
    async createPO(poData: any): Promise<any> {
        const selectedCompanyId = Number(localStorage.getItem('selectedCompanyId') || '');
        const final = { ...(poData || {}) } as any;
        if (!final.companyid && selectedCompanyId) final.companyid = selectedCompanyId;
        return this.post('/po', final);
    }

    async createPODraft(poData: any): Promise<any> {
        const selectedCompanyId = Number(localStorage.getItem('selectedCompanyId') || '');
        const final = { ...(poData || {}) } as any;
        if (!final.companyid && selectedCompanyId) final.companyid = selectedCompanyId;
        return this.post('/po/draft', final);
    }

    async updatePODraft(poId: number, poData: any): Promise<any> {
        const selectedCompanyId = Number(localStorage.getItem('selectedCompanyId') || '');
        const final = { ...(poData || {}) } as any;
        if (!final.companyid && selectedCompanyId) final.companyid = selectedCompanyId;
        return this.put(`/po/draft/${poId}`, final);
    }

    async discardPODraft(poId: number): Promise<any> {
        return this.delete(`/po/draft/${poId}`);
    }

    async getPO(id: number): Promise<any> {
        return this.get(`/po/${id}`);
    }

    /** Create PO: the charge types the company sends to SAP as freight ({ codes: { 'FREIGHT CHARGES': { code, name } } }). */
    async getPOChargeCodes(companyId: number): Promise<any> {
        return this.get('/po/charge-codes', { companyid: companyId });
    }

    async getMyPOs(): Promise<any> {
        const selectedCompanyId = Number(localStorage.getItem('selectedCompanyId') || '');
        const params = selectedCompanyId ? { companyId: selectedCompanyId } : {};
        return this.get('/po/my-pos', params);
    }

    async getMyPODrafts(): Promise<any> {
        const selectedCompanyId = Number(localStorage.getItem('selectedCompanyId') || '');
        const params = selectedCompanyId ? { companyId: selectedCompanyId } : {};
        return this.get('/po/my-drafts', params);
    }

    async getPendingApprovalsPO(): Promise<any> {
        const selectedCompanyId = Number(localStorage.getItem('selectedCompanyId') || '');
        const params = selectedCompanyId ? { companyId: selectedCompanyId } : {};
        return this.get('/po/pending-approvals', params);
    }

    async getApproverPOs(): Promise<any> {
        const selectedCompanyId = Number(localStorage.getItem('selectedCompanyId') || '');
        const params = selectedCompanyId ? { companyId: selectedCompanyId } : {};
        return this.get('/po/approver-tracking', params);
    }

    async submitPODraft(poId: number, payload?: any): Promise<any> {
        return this.post(`/po/${poId}/submit`, payload || {});
    }

    async movePOToCompleted(poId: number): Promise<any> {
        return this.post(`/po/${poId}/move-to-completed`);
    }

    async processPOApproval(poId: number, action: string, comments?: string): Promise<any> {
        return this.post(`/po/${poId}/process`, { action, comments });
    }

    async getPOWorkflowStatus(poId: number): Promise<any> {
        return this.get(`/po/${poId}/workflow`);
    }

    async getPOApprovalHistory(poId: number): Promise<any> {
        return this.get(`/po/${poId}/history`);
    }

    async resolvePOApprovalActionToken(token: string): Promise<any> {
        return this.get(`/po/email-action/token/${encodeURIComponent(token)}`);
    }

    async processPOApprovalActionToken(token: string, comments?: string): Promise<any> {
        return this.post(`/po/email-action/token/${encodeURIComponent(token)}/process`, { comments });
    }

    // ---- SAP-driven PO approval (e-mail links are public; the token is the key) ----
    async resolveSapPoApproval(token: string): Promise<any> {
        return this.get(`/sap-po-approval/token/${encodeURIComponent(token)}`, undefined, { timeout: 90000 });
    }

    /** The decision only: PRPO uses the SAP password an administrator saved for the approver. */
    async decideSapPoApproval(token: string, payload: { action: string; comments?: string }): Promise<any> {
        return this.post(`/sap-po-approval/token/${encodeURIComponent(token)}/decide`, payload, { timeout: 120000 });
    }

    // ---- My SAP B1 account (signed-in user; only administrators may change it) ----
    async getMySapAccount(): Promise<any> {
        return this.get('/sap-po-approval/my-sap-account');
    }

    async saveMySapAccount(payload: { companyId: number; sapUserCode: string; password: string; newPassword?: string }): Promise<any> {
        return this.put('/sap-po-approval/my-sap-account', payload);
    }

    async deleteMySapAccount(): Promise<any> {
        return this.delete('/sap-po-approval/my-sap-account');
    }

    // ---- Admin: SAP B1 passwords of the PRPO users (status only, never the passwords) ----
    async getSapPasswordUsers(): Promise<any> {
        return this.get('/sap-po-approval/admin/sap-passwords');
    }

    async getSapPasswordHistory(userId: number): Promise<any> {
        return this.get(`/sap-po-approval/admin/sap-passwords/${userId}/history`);
    }

    /** Set in SAP and save ('set'), or only check and save ('save'), a user's SAP password in ONE company. */
    async applySapPassword(userId: number, payload: { companyId: number; password: string; mode: 'set' | 'save' }): Promise<any> {
        return this.post(`/sap-po-approval/admin/sap-passwords/${userId}`, payload, { timeout: 120000 });
    }

    async deleteSapSavedPassword(credentialId: number): Promise<any> {
        return this.delete(`/sap-po-approval/admin/saved-passwords/${credentialId}`);
    }

    // ---- Admin: which PRPO user each SAP user belongs to (Users.SAPUserCode) ----
    async getSapUserLinks(companyId: number): Promise<any> {
        return this.get(`/sap-po-approval/admin/companies/${companyId}/sap-users`, undefined, { timeout: 120000 });
    }

    async linkSapUser(companyId: number, userId: number, sapUserCode: string): Promise<any> {
        return this.post(`/sap-po-approval/admin/companies/${companyId}/sap-users/link`, { userId, sapUserCode }, { timeout: 120000 });
    }

    async unlinkSapUser(companyId: number, userId: number, sapUserCode: string): Promise<any> {
        return this.post(`/sap-po-approval/admin/companies/${companyId}/sap-users/unlink`, { userId, sapUserCode });
    }

    sapPoApprovalAttachmentUrl(token: string, lineNum: number): string {
        return `${API_BASE_URL}/sap-po-approval/token/${encodeURIComponent(token)}/attachments/${Number(lineNum)}`;
    }

    async getSapApprovalWorkflows(companyId: number, refresh = false): Promise<any> {
        return this.get(`/sap-po-approval/admin/companies/${companyId}/workflows${refresh ? '?refresh=1' : ''}`, undefined, { timeout: 180000 });
    }

    async getSapApprovalEmailTemplates(): Promise<any> {
        return this.get('/sap-po-approval/admin/email-templates');
    }

    async getSapApprovalEmailTemplateChoices(): Promise<any> {
        return this.get('/sap-po-approval/email-templates/choices');
    }

    async createSapApprovalEmailTemplate(payload: { name: string; description?: string; options?: any; copyFromId?: number }): Promise<any> {
        return this.post('/sap-po-approval/admin/email-templates', payload);
    }

    async updateSapApprovalEmailTemplate(templateId: number, payload: { name?: string; description?: string; options?: any; isactive?: boolean; isdefault?: boolean }): Promise<any> {
        return this.put(`/sap-po-approval/admin/email-templates/${templateId}`, payload);
    }

    async deleteSapApprovalEmailTemplate(templateId: number): Promise<any> {
        return this.delete(`/sap-po-approval/admin/email-templates/${templateId}`);
    }

    async previewSapApprovalEmail(companyId: number, payload: { templateId?: number; options?: any; requestCode?: number }): Promise<any> {
        return this.post(`/sap-po-approval/admin/companies/${companyId}/email-preview`, payload, { timeout: 120000 });
    }

    async getSapApprovalRequests(companyId: number, params: { since?: string; status?: 'pending' | 'all' }): Promise<any> {
        const q = new URLSearchParams();
        if (params.since) q.set('since', params.since);
        if (params.status) q.set('status', params.status);
        return this.get(`/sap-po-approval/admin/companies/${companyId}/requests?${q.toString()}`, undefined, { timeout: 180000 });
    }

    async getSapApprovalLog(companyId: number): Promise<any> {
        return this.get(`/sap-po-approval/admin/companies/${companyId}/log`);
    }

    async retrySapApprovals(companyId: number, notificationId?: number): Promise<any> {
        return this.post(`/sap-po-approval/admin/companies/${companyId}/retry`, notificationId ? { notificationId } : {}, { timeout: 300000 });
    }

    async sendSapApprovalTestEmail(companyId: number): Promise<any> {
        return this.post(`/sap-po-approval/admin/companies/${companyId}/test-email`, {}, { timeout: 90000 });
    }

    async pollSapApprovals(companyId: number): Promise<any> {
        return this.post(`/sap-po-approval/admin/companies/${companyId}/poll`, {}, { timeout: 300000 });
    }

    // Settings > Accounts notifications: e-mail the accounts team when a PO is finally approved.
    async getAccountsNotifySettings(companyId: number): Promise<any> {
        return this.get(`/accounts-notifications/companies/${companyId}`, undefined, { timeout: 90000 });
    }

    async getAccountsNotifyLists(companyId: number): Promise<any> {
        return this.get(`/accounts-notifications/companies/${companyId}/lists`, undefined, { timeout: 90000 });
    }

    async getAccountsNotifyTermCounts(companyId: number): Promise<any> {
        return this.get(`/accounts-notifications/companies/${companyId}/term-counts`, undefined, { timeout: 90000 });
    }

    async matchAccountsNotifyRule(companyId: number, rule: any): Promise<any> {
        return this.post(`/accounts-notifications/companies/${companyId}/match`, { rule }, { timeout: 90000 });
    }

    async setAccountsNotifyEnabled(companyId: number, enabled: boolean): Promise<any> {
        return this.put(`/accounts-notifications/companies/${companyId}/enabled`, { enabled });
    }

    async createAccountsNotifyRule(companyId: number, rule: any): Promise<any> {
        return this.post(`/accounts-notifications/companies/${companyId}/rules`, rule);
    }

    async updateAccountsNotifyRule(ruleId: number, rule: any): Promise<any> {
        return this.put(`/accounts-notifications/rules/${ruleId}`, rule);
    }

    async setAccountsNotifyRuleActive(ruleId: number, active: boolean): Promise<any> {
        return this.put(`/accounts-notifications/rules/${ruleId}/active`, { active });
    }

    async deleteAccountsNotifyRule(ruleId: number): Promise<any> {
        return this.delete(`/accounts-notifications/rules/${ruleId}`);
    }

    async previewAccountsNotifyEmail(companyId: number, rule?: any): Promise<any> {
        return this.post(`/accounts-notifications/companies/${companyId}/preview`, { rule }, { timeout: 180000 });
    }

    async sendAccountsNotifyTestEmail(companyId: number, rule?: any): Promise<any> {
        return this.post(`/accounts-notifications/companies/${companyId}/test-email`, { rule }, { timeout: 180000 });
    }

    async getAccountsNotifySent(companyId: number): Promise<any> {
        return this.get(`/accounts-notifications/companies/${companyId}/sent`);
    }

    async resendAccountsNotification(notificationId: number): Promise<any> {
        return this.post(`/accounts-notifications/sent/${notificationId}/resend`, {}, { timeout: 180000 });
    }

    async resolvePOBulkApprovalToken(token: string): Promise<any> {
        return this.get(`/po/email-action/bulk/${encodeURIComponent(token)}`);
    }

    async processPOBulkApprovalToken(token: string, payload: { poIds: number[]; action: string; comments?: string }): Promise<any> {
        return this.post(`/po/email-action/bulk/${encodeURIComponent(token)}/process`, payload);
    }

    async uploadPOAttachments(poId: number, files: File[], onUploadProgress?: (e: any) => void): Promise<any> {
        const form = new FormData();
        for (const f of files) form.append('files', f);
        const response = await this.axiosInstance.post(`/po/${poId}/attachments/upload`, form, {
            headers: { 'Content-Type': 'multipart/form-data' },
            onUploadProgress,
        });
        return response.data;
    }

    async getPOAttachments(poId: number): Promise<any> {
        return this.get(`/po/${poId}/attachments`);
    }

    async deletePOAttachment(poId: number, attachmentId: number): Promise<any> {
        return this.delete(`/po/${poId}/attachments/${attachmentId}`);
    }

    getPOAttachmentDownloadUrl(poId: number, attachmentId: number): string {
        const token = localStorage.getItem('token') || '';
        const q = token ? `?token=${encodeURIComponent(token)}` : '';
        return `${API_BASE_URL}/po/${poId}/attachments/${attachmentId}/download${q}`;
    }

    getPOAttachmentViewUrl(poId: number, attachmentId: number): string {
        const token = localStorage.getItem('token') || '';
        const q = token ? `?token=${encodeURIComponent(token)}` : '';
        return `${API_BASE_URL}/po/${poId}/attachments/${attachmentId}/view${q}`;
    }

    subscribePOStatus(poId: number): EventSource {
        const token = localStorage.getItem('token') || '';
        const q = token ? `?token=${encodeURIComponent(token)}` : '';
        const url = `${API_BASE_URL}/po/${poId}/subscribe${q}`;
        const es = new EventSource(url);
        return es;
    }

    async copyFromPR(prId: number): Promise<any> {
        return this.post(`/po/copy-from-pr/${prId}`);
    }

    async globalSearch(query: string, options?: {
        types?: string;
        companyId?: number;
        databaseName?: string;
        limit?: number;
        offset?: number;
    }): Promise<any> {
        const params: any = { q: query };
        if (options) {
            if (options.types) params.types = options.types;
            if (options.companyId) params.companyId = options.companyId;
            if (options.databaseName) params.databaseName = options.databaseName;
            if (options.limit) params.limit = options.limit;
            if (options.offset) params.offset = options.offset;
        }
        return this.get('/search', params);
    }

    async getSearchAutocomplete(query: string, type?: string): Promise<any> {
        const params: any = { q: query };
        if (type) params.type = type;
        return this.get('/search/autocomplete', params);
    }

    async syncSearchIndex(): Promise<any> {
        return this.post('/search/sync');
    }

    async getSearchHealth(): Promise<any> {
        return this.get('/search/health');
    }

    // ============ Subcontract Methods ============

    // Subcontract CRUD
    async createSubcontract(data: any, options?: { postToSAP?: boolean }): Promise<any> {
        const selectedCompanyId = Number(localStorage.getItem('selectedCompanyId') || '');
        const final = { ...(data || {}) } as any;
        if (!final.companyid && selectedCompanyId) final.companyid = selectedCompanyId;
        final.posttosap = !!options?.postToSAP;
        const response = await this.axiosInstance.post('/subcontract', final, { timeout: 120000 });
        return response.data;
    }

    async getSubcontract(id: number): Promise<any> {
        const selectedCompanyId = Number(localStorage.getItem('selectedCompanyId') || '');
        const params = selectedCompanyId ? { companyId: selectedCompanyId } : {};
        return this.get(`/subcontract/${id}`, params);
    }

    async getNextSubcontractContractNumber(projectCode: string, contractDate?: string, companyId?: number, subcontractType?: string): Promise<any> {
        const selectedCompanyId = Number(localStorage.getItem('selectedCompanyId') || '');
        const params: any = {
            projectCode,
            companyId: companyId || selectedCompanyId || undefined,
        };
        if (contractDate) params.contractDate = contractDate;
        if (subcontractType) params.subcontractType = subcontractType;
        return this.get('/subcontract/next-contract-number', params);
    }

    async listSubcontracts(params?: any): Promise<any> {
        const selectedCompanyId = Number(localStorage.getItem('selectedCompanyId') || '');
        const p = { ...(params || {}) };
        if (!p.companyId && selectedCompanyId) p.companyId = selectedCompanyId;
        return this.get('/subcontract/list', p);
    }

    async getSubcontractStats(params?: any): Promise<any> {
        const selectedCompanyId = Number(localStorage.getItem('selectedCompanyId') || '');
        const p = { ...(params || {}) };
        if (!p.companyId && selectedCompanyId) p.companyId = selectedCompanyId;
        return this.get('/subcontract/stats', p);
    }

    async updateSubcontract(id: number, data: any): Promise<any> {
        const response = await this.axiosInstance.put(`/subcontract/${id}`, data, { timeout: 120000 });
        return response.data;
    }

    async postSubcontractToSAP(id: number): Promise<any> {
        const response = await this.axiosInstance.post(`/subcontract/${id}/post-to-sap`, undefined, { timeout: 120000 });
        return response.data;
    }

    // Subcontract Documents
    async updateDocumentReceived(scId: number, documentType: string, received: boolean): Promise<any> {
        return this.put(`/subcontract/${scId}/documents/${documentType}/received`, { received });
    }

    async uploadDocumentAttachment(scId: number, documentType: string, files: File[], onUploadProgress?: (e: any) => void): Promise<any> {
        const form = new FormData();
        for (const f of files) form.append('files', f);
        const response = await this.axiosInstance.post(`/subcontract/${scId}/documents/${documentType}/upload`, form, {
            headers: { 'Content-Type': 'multipart/form-data' },
            onUploadProgress,
        });
        return response.data;
    }

    async getDocumentAttachments(scId: number): Promise<any> {
        return this.get(`/subcontract/${scId}/documents/attachments`);
    }

    getDocumentAttachmentViewUrl(scId: number, attachmentId: number): string {
        const token = localStorage.getItem('token') || '';
        const base = this.axiosInstance.defaults.baseURL || '';
        const q = token ? `?token=${encodeURIComponent(token)}` : '';
        return `${base}/subcontract/${scId}/documents/attachments/${attachmentId}/view${q}`;
    }

    getDocumentAttachmentDownloadUrl(scId: number, attachmentId: number): string {
        const token = localStorage.getItem('token') || '';
        const base = this.axiosInstance.defaults.baseURL || '';
        const q = token ? `?token=${encodeURIComponent(token)}` : '';
        return `${base}/subcontract/${scId}/documents/attachments/${attachmentId}/download${q}`;
    }

    async deleteDocumentAttachment(scId: number, attachmentId: number): Promise<any> {
        return this.delete(`/subcontract/${scId}/documents/attachments/${attachmentId}`);
    }

    // Variations
    async createVariation(scId: number, data: any): Promise<any> {
        return this.post(`/subcontract/${scId}/variations`, data);
    }

    async getVariations(scId: number): Promise<any> {
        return this.get(`/subcontract/${scId}/variations`);
    }

    // Work Done
    async createWorkDone(scId: number, data: any): Promise<any> {
        return this.post(`/subcontract/${scId}/work-done`, data);
    }

    async getWorkDone(scId: number): Promise<any> {
        return this.get(`/subcontract/${scId}/work-done`);
    }

    // Payment Certificates
    async createPaymentCertificate(scId: number, data: any): Promise<any> {
        return this.post(`/subcontract/${scId}/payment-certificates`, data);
    }

    async getPaymentCertificates(scId: number): Promise<any> {
        return this.get(`/subcontract/${scId}/payment-certificates`);
    }

    async getPaymentCertificateById(scId: number, pcId: number): Promise<any> {
        return this.get(`/subcontract/${scId}/payment-certificates/${pcId}`);
    }

    async getPreviousPaymentCertificate(scId: number, pcId: number): Promise<any> {
        return this.get(`/subcontract/${scId}/payment-certificates/${pcId}/previous`);
    }

    async updatePaymentCertificate(scId: number, pcId: number, data: any): Promise<any> {
        return this.put(`/subcontract/${scId}/payment-certificates/${pcId}`, data);
    }

    async submitPaymentCertificateToFinance(scId: number, pcId: number): Promise<any> {
        return this.post(`/subcontract/${scId}/payment-certificates/${pcId}/submit`);
    }

    async postPaymentCertificateToSAP(scId: number, pcId: number): Promise<any> {
        return this.post(`/subcontract/${scId}/payment-certificates/${pcId}/post-to-sap`);
    }

    async getSubcontractDeductionReleasePreview(scId: number, pcId: number): Promise<any> {
        return this.get(`/subcontract/${scId}/payment-certificates/${pcId}/deduction-release-preview`);
    }

    async postSubcontractDeductionRelease(scId: number, pcId: number, lines: Array<{ key: string; amount: number }>): Promise<any> {
        return this.post(`/subcontract/${scId}/payment-certificates/${pcId}/deduction-releases`, { lines });
    }

    // ─── DocuSign Methods ─────────────────────────────────────────

    async sendDocuSignEnvelope(
        files: File[],
        mainDocumentIndex: number,
        signers: { name: string; email: string; order: number }[],
        emailSubject: string,
        emailBody: string,
        onUploadProgress?: (e: any) => void
    ): Promise<any> {
        const form = new FormData();
        for (const f of files) form.append('files', f);
        form.append('mainDocumentIndex', String(mainDocumentIndex));
        form.append('signers', JSON.stringify(signers));
        form.append('emailSubject', emailSubject);
        form.append('emailBody', emailBody);
        const response = await this.axiosInstance.post('/docusign/send', form, {
            headers: { 'Content-Type': 'multipart/form-data' },
            onUploadProgress,
        });
        return response.data;
    }

    async getDocuSignEnvelopes(): Promise<any> {
        return this.get('/docusign/envelopes');
    }

    async getDocuSignEnvelope(localId: number): Promise<any> {
        return this.get(`/docusign/envelopes/${localId}`);
    }

    async getDocuSignEnvelopeStatus(localId: number): Promise<any> {
        return this.get(`/docusign/envelopes/${localId}/status`);
    }

    getDocuSignDownloadUrl(localId: number): string {
        const token = localStorage.getItem('token') || '';
        const q = token ? `?token=${encodeURIComponent(token)}` : '';
        return `${API_BASE_URL}/docusign/envelopes/${localId}/download${q}`;
    }

    async getPODocuSignStatus(poId: number): Promise<any> {
        return this.get(`/po/${poId}/docusign-status`);
    }

    async syncDocuSignPOs(): Promise<any> {
        return this.post('/docusign/sync-pos');
    }

    // =========================================================================
    // Payment Request methods (Phase 12)
    // =========================================================================

    async listPaymentRequests(params: any = {}): Promise<any> {
        const selectedCompanyId = Number(localStorage.getItem('selectedCompanyId') || '');
        const merged = selectedCompanyId ? { companyId: selectedCompanyId, ...params } : params;
        return this.get('/payment-requests', merged);
    }

    async getPaymentRequest(id: number): Promise<any> {
        return this.get(`/payment-requests/${id}`);
    }

    async createPaymentRequest(input: any): Promise<any> {
        const selectedCompanyId = Number(localStorage.getItem('selectedCompanyId') || '');
        const final = { ...(input || {}) } as any;
        if (!final.companyId && selectedCompanyId) final.companyId = selectedCompanyId;
        return this.post('/payment-requests', final);
    }

    async updatePaymentRequest(id: number, patch: any): Promise<any> {
        return this.put(`/payment-requests/${id}`, patch);
    }

    async deletePaymentRequest(id: number): Promise<any> {
        return this.delete(`/payment-requests/${id}`);
    }

    async submitPaymentRequest(id: number): Promise<any> {
        return this.post(`/payment-requests/${id}/submit`);
    }

    async apSubmitPaymentRequest(id: number, input: any): Promise<any> {
        return this.post(`/payment-requests/${id}/ap-submit`, input);
    }

    async approvePaymentRequest(id: number, comments?: string): Promise<any> {
        return this.post(`/payment-requests/${id}/approve`, { comments });
    }

    async rejectPaymentRequest(id: number, comments: string): Promise<any> {
        return this.post(`/payment-requests/${id}/reject`, { comments });
    }

    async returnPaymentRequest(id: number, comments: string): Promise<any> {
        return this.post(`/payment-requests/${id}/return`, { comments });
    }

    async postPaymentRequestToSAP(id: number): Promise<any> {
        return this.post(`/payment-requests/${id}/post-to-sap`);
    }

    async retryPaymentRequestSAP(id: number): Promise<any> {
        return this.post(`/payment-requests/${id}/retry-sap`);
    }

    async overridePaymentRequest(id: number, field: string, newValue: any, reason: string): Promise<any> {
        return this.post(`/payment-requests/${id}/override`, { field, newValue, reason });
    }

    async previewPaymentRequestSAP(id: number): Promise<any> {
        return this.get(`/payment-requests/${id}/sap-preview`);
    }

    async getPaymentRequestDocuSignStatus(id: number): Promise<any> {
        return this.get(`/payment-requests/${id}/docusign-status`);
    }

    async getPaymentRequestStats(): Promise<any> {
        const selectedCompanyId = Number(localStorage.getItem('selectedCompanyId') || '');
        const params = selectedCompanyId ? { companyId: selectedCompanyId } : {};
        return this.get('/payment-requests/stats', params);
    }

    paymentRequestPdfUrl(id: number): string {
        return `${API_BASE_URL}/payment-requests/${id}/pdf`;
    }

    /**
     * Fetches the Payment Request PDF as a blob (using the axios auth interceptor
     * to attach the JWT) and opens it in a new browser tab via an object URL.
     * Falls back to triggering a download if popup is blocked.
     *
     * Why blob: the /pdf endpoint requires an Authorization header. Plain
     * window.open(url) would be a top-level navigation that doesn't include the
     * JWT, so the auth middleware rejects it with 401.
     */
    async openPaymentRequestPdf(id: number): Promise<void> {
        const response = await this.axiosInstance.get(`/payment-requests/${id}/pdf`, {
            responseType: 'blob',
        });
        const blob = new Blob([response.data], { type: 'application/pdf' });
        const blobUrl = URL.createObjectURL(blob);
        const opened = window.open(blobUrl, '_blank');
        if (!opened) {
            // Popup blocked — fall back to a download link.
            const a = document.createElement('a');
            a.href = blobUrl;
            a.download = `payment-request-${id}.pdf`;
            document.body.appendChild(a);
            a.click();
            document.body.removeChild(a);
        }
        // Revoke after a delay to give the browser time to load the blob.
        setTimeout(() => URL.revokeObjectURL(blobUrl), 60_000);
    }

    async uploadPaymentRequestAttachment(
        id: number,
        file: File,
        attachmentType = 'Other',
        stage = 'Initiator',
        onUploadProgress?: (e: any) => void,
    ): Promise<any> {
        const form = new FormData();
        form.append('file', file);
        form.append('attachmentType', attachmentType);
        form.append('stage', stage);
        const response = await this.axiosInstance.post(
            `/payment-requests/${id}/attachments`,
            form,
            {
                headers: { 'Content-Type': 'multipart/form-data' },
                onUploadProgress,
            },
        );
        return response.data;
    }

    async listPaymentRequestAttachments(id: number): Promise<any> {
        return this.get(`/payment-requests/${id}/attachments`);
    }

    async deletePaymentRequestAttachment(id: number, attachmentId: number): Promise<any> {
        return this.delete(`/payment-requests/${id}/attachments/${attachmentId}`);
    }

    paymentRequestAttachmentUrl(id: number, attachmentId: number): string {
        return `${API_BASE_URL}/payment-requests/${id}/attachments/${attachmentId}`;
    }

    /**
     * Download an attachment as a Blob via the axios instance (so the JWT
     * auth interceptor runs) and trigger a browser save / inline open.
     */
    async downloadPaymentRequestAttachment(
        id: number,
        attachmentId: number,
        filename: string,
        mimeType?: string,
    ): Promise<void> {
        const response = await this.axiosInstance.get(
            `/payment-requests/${id}/attachments/${attachmentId}`,
            { responseType: 'blob' },
        );
        const blob = new Blob([response.data], { type: mimeType || 'application/octet-stream' });
        const blobUrl = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = blobUrl;
        a.download = filename;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        setTimeout(() => URL.revokeObjectURL(blobUrl), 60_000);
    }

    // Lookups

    async lookupPRVendors(params: any): Promise<any> {
        return this.get('/payment-requests/lookups/vendors', params);
    }

    async lookupPRPurchaseOrders(params: any): Promise<any> {
        return this.get('/payment-requests/lookups/purchase-orders', params);
    }

    async lookupPRGRPOs(params: any): Promise<any> {
        return this.get('/payment-requests/lookups/grpos', params);
    }

    async lookupPRProjects(companyId: number): Promise<any> {
        return this.get('/payment-requests/lookups/projects', { companyId });
    }

    async lookupPRCostCenters(companyId: number): Promise<any> {
        return this.get('/payment-requests/lookups/cost-centers', { companyId });
    }

    async lookupPRPOPaymentStatus(companyId: number, poDocEntry: number): Promise<any> {
        return this.get('/payment-requests/lookups/po-payment-status', { companyId, poDocEntry });
    }

    async getAutoInvoiceProjects(companyId: number): Promise<any> {
        return this.get('/auto-invoice/projects', { companyId }, { timeout: 120000 });
    }

    async getAutoInvoiceLookups(companyId: number): Promise<any> {
        try {
            return await this.get('/auto-invoice/lookups', { companyId }, { timeout: 120000 });
        } catch (error: any) {
            if (error?.response?.status !== 404) throw error;

            const [projects, dim1, dim2, dim3, dim4, dim5, accounts] = await Promise.all([
                this.getAutoInvoiceProjects(companyId),
                this.get('/master-data/distribution-rules', { companyid: companyId, dimension: 1 }),
                this.get('/master-data/distribution-rules', { companyid: companyId, dimension: 2 }),
                this.get('/master-data/distribution-rules', { companyid: companyId, dimension: 3 }),
                this.get('/master-data/distribution-rules', { companyid: companyId, dimension: 4 }),
                this.get('/master-data/distribution-rules', { companyid: companyId, dimension: 5 }),
                this.getAccounts({ companyId, limit: 500 }),
            ]);

            const normalizeDimension = (res: any) => (Array.isArray(res?.data) ? res.data : []).map((row: any) => ({
                code: String(row?.FactorCode || row?.Code || row?.keycode || '').trim(),
                name: String(row?.FactorDescription || row?.Name || row?.Description || '').trim(),
            })).filter((row: any) => row.code);

            return {
                success: true,
                data: {
                    projects: Array.isArray((projects as any)?.data) ? (projects as any).data : [],
                    dimensions: {
                        dim1: normalizeDimension(dim1),
                        dim2: normalizeDimension(dim2),
                        dim3: normalizeDimension(dim3),
                        dim4: normalizeDimension(dim4),
                        dim5: normalizeDimension(dim5),
                    },
                    accounts: (Array.isArray((accounts as any)?.data) ? (accounts as any).data : []).map((row: any) => ({
                        code: String(row?.Code || row?.AcctCode || row?.keycode || '').trim(),
                        name: String(row?.Name || row?.AcctName || row?.AccountName || '').trim(),
                    })).filter((row: any) => row.code),
                },
            };
        }
    }

    async getAutoInvoiceCustomers(companyId: number, q?: string): Promise<any> {
        return this.get('/sap/customers', { companyId, q: q || '', limit: 200 }, { timeout: 120000 });
    }

    async searchAutoInvoiceGRPOs(params: {
        companyId: number;
        projectCode: string;
        projectCodes?: string[];
        fromDate: string;
        toDate: string;
        marginPercent: number;
        customerCode?: string;
        processMode?: 'direct' | 'final-unbilled';
    }): Promise<any> {
        return this.get('/auto-invoice/grpos', {
            ...params,
            projectCodes: Array.isArray(params.projectCodes) ? params.projectCodes.join(',') : undefined,
        }, { timeout: 240000 });
    }

    async getAutoInvoiceHistory(params: {
        companyId: number;
        projectCode?: string;
        customerCode?: string;
        arDocNum?: string;
        goodsIssueDocNum?: string;
        fromDate?: string;
        toDate?: string;
        q?: string;
        page?: number;
        limit?: number;
    }): Promise<any> {
        return this.get('/auto-invoice/history', params, { timeout: 120000 });
    }

    async getAutoInvoiceSalesOrderHistory(companyId: number, salesOrderDocEntry: number): Promise<any> {
        return this.get(`/auto-invoice/sales-orders/${salesOrderDocEntry}/history`, { companyId }, { timeout: 120000 });
    }

    async getAutoInvoiceManualJournalHistory(companyId: number, journalEntryDocNum: number): Promise<any> {
        return this.get(`/auto-invoice/manual-journal-entries/${journalEntryDocNum}/history`, { companyId }, { timeout: 120000 });
    }

    async processAutoInvoice(payload: {
        companyId: number;
        projectCode: string;
        projectCodes?: string[];
        arHeaderProjectCode?: string;
        invoiceType: 'charge-back' | 'special-project';
        invoiceLineMode: 'single' | 'multiple';
        processMode?: 'direct' | 'final-unbilled';
        draftGrouping?: 'combined' | 'separate';
        fromDate: string;
        toDate: string;
        marginPercent: number;
        kpiDeduction?: number;
        customerCode: string;
        selections: Array<{
            transactionType?: 'GRPO' | 'Sales Order' | 'Manual JE';
            grpoDocEntry?: number;
            salesOrderDocEntry?: number;
            journalEntryDocNum?: number;
            manualJEEntry?: boolean;
            debitAccountCode?: string | null;
            creditAccountCode?: string | null;
            baseAmount?: number;
            invoiceAmount?: number;
            projectCode?: string | null;
            dim1?: string | null;
            dim2?: string | null;
            dim3?: string | null;
            dim4?: string | null;
            dim5?: string | null;
            cogsAccountCode?: string | null;
        }>;
    }): Promise<any> {
        return this.post('/auto-invoice/process', payload, { timeout: 240000 });
    }

    // GRPO (Goods Receipt PO). Reads are live from SAP, so the timeouts are generous.
    async getGrpoLookups(companyId: number): Promise<any> {
        return this.get('/grpo/lookups', { companyId }, { timeout: 60000 });
    }

    async searchGrpoVendors(companyId: number, q: string): Promise<any> {
        return this.get('/grpo/vendors', { companyId, q }, { timeout: 60000 });
    }

    async searchGrpoPurchaseOrders(params: {
        companyId: number;
        projectCode?: string;
        cardCode?: string;
        docNum?: string;
    }): Promise<any> {
        return this.get('/grpo/purchase-orders', params, { timeout: 120000 });
    }

    async getGrpoPurchaseOrder(companyId: number, docEntry: number): Promise<any> {
        return this.get(`/grpo/purchase-orders/${docEntry}`, { companyId }, { timeout: 120000 });
    }

    async submitGrpo(payload: {
        companyId: number;
        poDocEntry: number;
        submissionKey: string;
        docDate?: string;
        taxDate?: string;
        vendorRef?: string;
        remarks?: string;
        lines: Array<{ lineNum: number; quantity: number }>;
    }): Promise<any> {
        return this.post('/grpo/receipts', payload, { timeout: 240000 });
    }

    async getGrpoHistory(params: { companyId: number; status?: string; q?: string; page?: number; limit?: number }): Promise<any> {
        return this.get('/grpo/receipts', params, { timeout: 60000 });
    }

    // ---- New Item Requests (Warehouse > Create New Item) --------------------
    async getItemRequestCapabilities(): Promise<any> {
        return this.get('/item-requests/capabilities', undefined, { timeout: 30000 });
    }

    async getItemRequestLookups(companyId: number): Promise<any> {
        return this.get('/item-requests/lookups', { companyId }, { timeout: 120000 });
    }

    async getItemRequestNextCode(companyId: number, groupCode: number, requestId?: number): Promise<any> {
        return this.get('/item-requests/next-code', { companyId, groupCode, requestId }, { timeout: 60000 });
    }

    async getSimilarItems(companyId: number, name: string): Promise<any> {
        return this.get('/item-requests/similar', { companyId, name }, { timeout: 60000 });
    }

    async searchItemRequestAccounts(companyId: number, q: string): Promise<any> {
        return this.get('/item-requests/accounts', { companyId, q }, { timeout: 60000 });
    }

    async getItemGLPreview(payload: {
        companyId: number;
        itemGroup?: number | null;
        itemClass?: string | null;
        itemCode?: string | null;
        inventoryItem?: boolean;
    }): Promise<any> {
        return this.post('/item-requests/gl-preview', payload, { timeout: 120000 });
    }

    async listItemRequests(companyId: number, scope: 'mine' | 'waiting' | 'all'): Promise<any> {
        return this.get('/item-requests', { companyId, scope }, { timeout: 60000 });
    }

    async getItemRequest(id: number): Promise<any> {
        return this.get(`/item-requests/${id}`, undefined, { timeout: 60000 });
    }

    async createItemRequest(payload: any): Promise<any> {
        return this.post('/item-requests', payload, { timeout: 120000 });
    }

    async updateItemRequest(id: number, payload: any): Promise<any> {
        return this.put(`/item-requests/${id}`, payload);
    }

    async cancelItemRequest(id: number, comment?: string): Promise<any> {
        return this.post(`/item-requests/${id}/cancel`, { comment }, { timeout: 60000 });
    }

    async financeReviewItemRequest(id: number, payload: { action: 'approve' | 'return' | 'reject'; comment?: string; overrides?: any[] }): Promise<any> {
        return this.post(`/item-requests/${id}/finance`, payload, { timeout: 120000 });
    }

    async procurementDecideItemRequest(id: number, payload: { action: 'approve' | 'return' | 'reject'; comment?: string }): Promise<any> {
        return this.post(`/item-requests/${id}/procurement`, payload, { timeout: 240000 });
    }

    async retryItemRequest(id: number): Promise<any> {
        return this.post(`/item-requests/${id}/retry`, {}, { timeout: 240000 });
    }

    // ---- G/L Rules Year Copy (Settings, Admin) ------------------------------
    async getGlRuleYears(companyId: number): Promise<any> {
        return this.get('/gl-rules/years', { companyId }, { timeout: 120000 });
    }

    async previewGlRuleCopy(payload: { companyId: number; fromPeriod: string; toPeriod: string }): Promise<any> {
        return this.post('/gl-rules/copy/preview', payload, { timeout: 180000 });
    }

    async startGlRuleCopy(payload: { companyId: number; fromPeriod: string; toPeriod: string }): Promise<any> {
        return this.post('/gl-rules/copy', payload, { timeout: 120000 });
    }

    async listGlRuleCopyRuns(companyId: number): Promise<any> {
        return this.get('/gl-rules/copy/runs', { companyId }, { timeout: 60000 });
    }

    async getGlRuleCopyRun(runId: number): Promise<any> {
        return this.get(`/gl-rules/copy/runs/${runId}`, undefined, { timeout: 60000 });
    }

    async retryGrpoGoodsIssue(companyId: number, postingId: number): Promise<any> {
        return this.post(`/grpo/receipts/${postingId}/retry-goods-issue`, { companyId }, { timeout: 240000 });
    }

    // Admin

    async getPaymentRequestWorkflow(companyId: number): Promise<any> {
        return this.get(`/admin/payment-request-workflow/${companyId}`);
    }

    async getPaymentRequestWorkflowAnchors(): Promise<any> {
        return this.get(`/admin/payment-request-workflow/anchors`);
    }

    async getPaymentRequestWorkflowEligibleUsers(
        companyId: number,
        role: string,
    ): Promise<any> {
        return this.get(
            `/admin/payment-request-workflow/${companyId}/eligible-users`,
            { role },
        );
    }

    async updatePaymentRequestWorkflow(
        companyId: number,
        rows: any[],
    ): Promise<any> {
        return this.put(`/admin/payment-request-workflow/${companyId}`, { rows });
    }

    async resyncPaymentRequestWorkflowSigningGroups(
        companyId: number,
    ): Promise<any> {
        return this.post(
            `/admin/payment-request-workflow/${companyId}/resync-signing-groups`,
            {},
        );
    }

    async seedPaymentRequestWorkflow(companyId: number): Promise<any> {
        return this.post(`/admin/payment-request-workflow/seed/${companyId}`);
    }

    // ===================== Sales Web Portal =====================
    // Phase 0: configuration, discount thresholds, customer assignments, and the
    // SAP customer picker. companyId is passed explicitly (the request
    // interceptor only auto-injects it for /sap, /item-master and /schedules).

    async getSalesSettings(companyId?: number): Promise<any> {
        return this.get('/sales/config/settings', companyId ? { companyId } : undefined);
    }

    async updateSalesSetting(settingKey: string, settingValue: string, companyId?: number): Promise<any> {
        return this.put('/sales/config/settings', { settingKey, settingValue, companyId: companyId ?? null });
    }

    async getSalesThresholds(companyId?: number): Promise<any> {
        return this.get('/sales/config/thresholds', companyId ? { companyId } : undefined);
    }

    async upsertSalesThreshold(payload: {
        scopeType: string;
        scopeCode: string;
        maxDiscountPct: number;
        companyId?: number | null;
    }): Promise<any> {
        return this.put('/sales/config/thresholds', payload);
    }

    async deleteSalesThreshold(thresholdId: number): Promise<any> {
        return this.delete(`/sales/config/thresholds/${thresholdId}`);
    }

    async getSalespeople(): Promise<any> {
        return this.get('/sales/config/salespeople');
    }

    async getSalesAssignments(userId: number, companyId?: number): Promise<any> {
        return this.get(`/sales/config/assignments/${userId}`, companyId ? { companyId } : undefined);
    }

    async assignSalesCustomer(payload: {
        userId: number;
        companyId: number;
        cardCode: string;
        cardName?: string;
    }): Promise<any> {
        return this.post('/sales/config/assignments', payload);
    }

    /**
     * Bulk salesperson->customer assignment (Excel import on the settings page).
     * The workbook is parsed client-side; the server re-validates every row and
     * returns per-row errors alongside the counts it applied.
     */
    async bulkAssignSalesCustomers(payload: {
        companyId: number;
        rows: { rowNum?: number; username?: string; userId?: number; cardCode: string; cardName?: string }[];
    }): Promise<any> {
        return this.post('/sales/config/assignments/bulk', payload);
    }

    async removeSalesAssignment(assignmentId: number): Promise<any> {
        return this.delete(`/sales/config/assignments/${assignmentId}`);
    }

    async getSalesCustomers(companyId: number, search?: string): Promise<any> {
        return this.get('/sales/customers', { companyId, search });
    }

    async getSalesPriceLists(companyId?: number): Promise<any> {
        return this.get('/sales/config/pricelists', { companyId });
    }

    async createSalesPriceList(payload: { companyId?: number; priceListName: string; basePriceList?: number; factor?: number }): Promise<any> {
        return this.post('/sales/config/pricelists', payload);
    }

    async updateSalesPriceList(priceListNo: number, payload: { companyId?: number; priceListName: string }): Promise<any> {
        return this.patch(`/sales/config/pricelists/${priceListNo}`, payload);
    }

    async deleteSalesPriceList(priceListNo: number, companyId?: number): Promise<any> {
        return this.delete(`/sales/config/pricelists/${priceListNo}?companyId=${companyId ?? ''}`);
    }

    async getSalesPriceListItems(priceListNo: number, companyId?: number, search?: string): Promise<any> {
        return this.get(`/sales/config/pricelists/${priceListNo}/items`, { companyId, search });
    }

    async setSalesItemPrice(priceListNo: number, itemCode: string, payload: { companyId?: number; price: number; currency?: string }): Promise<any> {
        return this.put(`/sales/config/pricelists/${priceListNo}/items/${encodeURIComponent(itemCode)}`, payload);
    }

    // ---- Live Stock Check (Phase 1) ----
    async getSalesStock(
        companyId: number,
        params: {
            search?: string; warehouseCode?: string;
            category?: string; manufacturer?: string; brand2?: string;
        } = {},
    ): Promise<any> {
        return this.get('/sales/stock', { companyId, ...params });
    }

    async getSalesStockFilters(companyId: number): Promise<any> {
        return this.get('/sales/stock/filters', { companyId });
    }

    async getSalesStockExpiry(companyId: number, itemCode: string): Promise<any> {
        return this.get('/sales/stock/expiry', { companyId, itemCode });
    }

    async getSalesExpiryReport(companyId: number, params: { windowDays?: number; search?: string } = {}): Promise<any> {
        return this.get('/sales/stock/expiry-report', { companyId, ...params });
    }

    async getSalesStockLive(companyId: number, itemCode: string, warehouseCode?: string): Promise<any> {
        return this.get('/sales/stock/live', { companyId, itemCode, warehouseCode: warehouseCode || undefined });
    }

    // ---- Customer SOA (Phase 2) ----
    /** Consolidated sales-order report for a date window (inclusive). */
    /** Warehouse picking list: open SAP sales order lines due in [from, to], by item. */
    async getPickingList(companyId: number, from: string, to: string): Promise<any> {
        return this.get('/warehouse/picking-list', { companyId, from, to });
    }

    /** Units an item may be sold in; one entry means no choice to offer. */
    async getSalesItemUoms(companyId: number, itemCode: string): Promise<any> {
        return this.get('/sales/orders/item-uoms', { companyId, itemCode });
    }

    async getSalesOrderReport(
        companyId: number, from: string, to: string, status?: string,
    ): Promise<any> {
        return this.get('/sales/orders/report', { companyId, from, to, status });
    }

    // ---- A/R invoice printing (Epson LQ-2190 via the office print agent) ----
    async getInvoicePrintConfig(companyId: number): Promise<any> {
        return this.get('/sales/invoices/print-config', { companyId });
    }

    async getInvoicePrintCustomers(companyId: number, search?: string): Promise<any> {
        return this.get('/sales/invoices/customers', { companyId, search });
    }

    async searchInvoicesForPrint(companyId: number, filters: Record<string, string | undefined>): Promise<any> {
        return this.get('/sales/invoices', { companyId, ...filters }, { timeout: 300000 });
    }

    async previewInvoicePrint(companyId: number, docEntry: number): Promise<any> {
        return this.get(`/sales/invoices/${docEntry}/preview`, { companyId }, { timeout: 120000 });
    }

    /** docs: the selected invoices with their number and customer (shown on the print jobs until prepared). */
    async printInvoices(companyId: number, printerId: number, docs: Array<Record<string, unknown>>): Promise<any> {
        return this.post('/sales/invoices/print', { companyId, printerId, docs }, { timeout: 300000 });
    }

    async getInvoicePrintJobs(companyId: number, mine?: boolean): Promise<any> {
        return this.get('/sales/invoices/print-jobs', { companyId, mine: mine ? 'true' : undefined });
    }

    async cancelInvoicePrintJob(companyId: number, jobId: number): Promise<any> {
        return this.post(`/sales/invoices/print-jobs/${jobId}/cancel`, { companyId });
    }

    async reprintInvoicePrintJob(companyId: number, jobId: number): Promise<any> {
        return this.post(`/sales/invoices/print-jobs/${jobId}/reprint`, { companyId });
    }

    async getInvoicePrinters(companyId: number): Promise<any> {
        return this.get('/sales/invoices/printers', { companyId });
    }

    async saveInvoicePrinter(companyId: number, printer: Record<string, unknown>, printerId?: number): Promise<any> {
        return printerId
            ? this.put(`/sales/invoices/printers/${printerId}?companyId=${companyId}`, printer)
            : this.post(`/sales/invoices/printers?companyId=${companyId}`, printer);
    }

    async getPrintAgents(): Promise<any> {
        return this.get('/sales/invoices/print-agents');
    }

    async createPrintAgent(name: string): Promise<any> {
        return this.post('/sales/invoices/print-agents', { name });
    }

    async setPrintAgentActive(agentId: number, isActive: boolean): Promise<any> {
        return this.patch(`/sales/invoices/print-agents/${agentId}`, { isActive });
    }

    async getPrintAgentReadme(): Promise<any> {
        return this.get('/sales/invoices/print-agent/readme');
    }

    // ---- Sales order printing (dot-matrix form + office laser, same print agent) ----
    async getOrderPrintConfig(companyId: number): Promise<any> {
        return this.get('/sales/order-print/config', { companyId });
    }

    async getOrderPrintCustomers(companyId: number, search?: string): Promise<any> {
        return this.get('/sales/order-print/customers', { companyId, search });
    }

    async searchOrdersForPrint(companyId: number, filters: Record<string, string | undefined>): Promise<any> {
        return this.get('/sales/order-print', { companyId, ...filters }, { timeout: 300000 });
    }

    async previewOrderPrint(companyId: number, docEntry: number): Promise<any> {
        return this.get(`/sales/order-print/${docEntry}/preview`, { companyId }, { timeout: 120000 });
    }

    /** The laser printout of a sales order (A4 PDF), for the screen. */
    async getOrderPrintPdf(companyId: number, docEntry: number): Promise<Blob> {
        const response = await this.axiosInstance.get(`/sales/order-print/${docEntry}/pdf`, {
            params: { companyId },
            responseType: 'blob',
            timeout: 120000,
        });
        return response.data as Blob;
    }

    async printOrders(companyId: number, printerIds: number[], docs: Array<Record<string, unknown>>): Promise<any> {
        return this.post('/sales/order-print/print', { companyId, printerIds, docs }, { timeout: 300000 });
    }

    async getOrderPrintJobs(companyId: number, mine?: boolean): Promise<any> {
        return this.get('/sales/order-print/jobs', { companyId, mine: mine ? 'true' : undefined });
    }

    async cancelOrderPrintJob(companyId: number, jobId: number): Promise<any> {
        return this.post(`/sales/order-print/jobs/${jobId}/cancel`, { companyId });
    }

    async reprintOrderPrintJob(companyId: number, jobId: number): Promise<any> {
        return this.post(`/sales/order-print/jobs/${jobId}/reprint`, { companyId });
    }

    async getSoaCustomers(companyId: number, search?: string): Promise<any> {
        return this.get('/sales/soa/customers', { companyId, search });
    }

    /** `basis` selects which date the aging is measured from ('invoice' default, or 'due'). */
    async getCustomerSoa(
        companyId: number, cardCode: string, live?: boolean,
        basis?: 'invoice' | 'due', consolidated?: boolean,
        period?: { fromDate?: string; toDate?: string },
    ): Promise<any> {
        return this.get(`/sales/soa/${encodeURIComponent(cardCode)}`, {
            companyId, live: live ? 'true' : undefined, basis,
            consolidated: consolidated ? 'true' : undefined,
            fromDate: period?.fromDate || undefined,
            toDate: period?.toDate || undefined,
        });
    }

    // The PDF takes the same basis AND the same source (synced vs live) so the
    // document always matches the screen it was downloaded from.
    async getCustomerSoaPdf(
        companyId: number, cardCode: string, basis?: 'invoice' | 'due',
        live?: boolean, consolidated?: boolean,
        period?: { fromDate?: string; toDate?: string },
    ): Promise<Blob> {
        const response = await this.axiosInstance.get(
            `/sales/soa/${encodeURIComponent(cardCode)}/pdf`,
            {
                params: {
                    companyId, basis,
                    live: live ? 'true' : undefined,
                    consolidated: consolidated ? 'true' : undefined,
                    fromDate: period?.fromDate || undefined,
                    toDate: period?.toDate || undefined,
                },
                responseType: 'blob',
            },
        );
        return response.data as Blob;
    }

    // ---- Sales Orders (Phase 3) ----
    async getOrderCustomers(companyId: number, search?: string): Promise<any> {
        return this.get('/sales/orders/customers', { companyId, search });
    }

    async searchOrderItems(companyId: number, search?: string): Promise<any> {
        return this.get('/sales/orders/items', { companyId, search });
    }

    async salesOrderPriceLookup(companyId: number, cardCode: string, itemCode: string): Promise<any> {
        return this.get('/sales/orders/price-lookup', { companyId, cardCode, itemCode });
    }

    async listSalesOrders(companyId: number, status?: string, cardCode?: string): Promise<any> {
        return this.get('/sales/orders', { companyId, status, cardCode });
    }

    async getSalesOrder(orderId: number): Promise<any> {
        return this.get(`/sales/orders/${orderId}`);
    }

    async createSalesOrder(payload: any): Promise<any> {
        return this.post('/sales/orders', payload);
    }

    async updateSalesOrder(orderId: number, payload: any): Promise<any> {
        return this.put(`/sales/orders/${orderId}`, payload);
    }

    async submitSalesOrder(orderId: number): Promise<any> {
        return this.post(`/sales/orders/${orderId}/submit`, {});
    }

    // ---- Sales Order approvals (Phase 4) ----
    async getApprovalQueue(companyId: number, opts: { belowFloorOnly?: boolean; status?: 'PendingApproval' | 'Failed' } = {}): Promise<any> {
        return this.get('/sales/approvals', { companyId, ...opts });
    }

    /**
     * Approve an order. `priceEdits` carries approver price overrides (APR-07);
     * the server re-validates every price against a fresh SAP floor and audits
     * the change — the values sent here are only proposals.
     */
    async approveSalesOrder(orderId: number, priceEdits?: Array<{ lineId: number; unitPrice: number }>): Promise<any> {
        return this.post(`/sales/approvals/${orderId}/approve`, priceEdits?.length ? { priceEdits } : {});
    }

    async rejectSalesOrder(orderId: number, comment: string): Promise<any> {
        return this.post(`/sales/approvals/${orderId}/reject`, { comment });
    }

    async bulkApproveSalesOrders(ids: number[]): Promise<any> {
        return this.post('/sales/approvals/bulk-approve', { ids });
    }

    async retrySalesOrderSap(orderId: number): Promise<any> {
        return this.post(`/sales/approvals/${orderId}/retry-sap`, {});
    }

    async cancelFailedSalesOrder(orderId: number, reason: string): Promise<any> {
        return this.post(`/sales/approvals/${orderId}/cancel`, { reason });
    }

    // ---- Warehouse Receiving (GS1 scanning) ----
    /** Resolve a scanned 1D barcode / DataMatrix GTIN to an item via the local sync layer. */
    async resolveScan(companyId: number, params: { barcode?: string; gtin?: string }): Promise<any> {
        return this.get('/warehouse/resolve', { companyId, ...params });
    }

    /** List open source documents (open POs for Receiving, open SOs for Delivery). */
    async getWarehouseDocuments(companyId: number, mode: 'Receiving' | 'Delivery', search?: string, includeReceived?: boolean): Promise<any> {
        return this.get('/warehouse/documents', { companyId, mode, ...(search ? { search } : {}), ...(includeReceived ? { includeReceived: true } : {}) });
    }

    /** Load one source document with its open lines. */
    async getWarehouseDocumentLines(
        companyId: number,
        docType: 'PurchaseOrder' | 'SalesOrder',
        doc: { docEntry?: number; docNum?: string }
    ): Promise<any> {
        return this.get('/warehouse/documents/lines', { companyId, docType, ...doc });
    }

    /** Search the local item master (for the map-or-create review queue). */
    async searchWarehouseItems(companyId: number, search?: string): Promise<any> {
        return this.get('/warehouse/items', { companyId, ...(search ? { search } : {}) });
    }

    /** Map a scanned barcode to an existing item, or capture a new-item request. */
    async warehouseMapOrCreate(body: {
        companyId: number;
        code: string;
        codeType: 'barcode' | 'gtin';
        itemCode?: string;
        itemDescription?: string;
        source: 'map' | 'create';
        newItemFields?: Record<string, unknown>;
    }): Promise<any> {
        return this.post('/warehouse/barcode-map', body);
    }

    /** Barcode links awaiting write-back to SAP OBCD. */
    async getWarehousePendingSync(companyId: number): Promise<any> {
        return this.get('/warehouse/barcode-map/pending', { companyId });
    }

    /** Push pending barcode links to SAP OBCD (gated server-side). */
    async syncWarehouseBarcodes(companyId: number): Promise<any> {
        return this.post('/warehouse/barcode-map/sync', { companyId });
    }

    /** Option 2 — idempotent batch import of confirmed lot entries into the Item Master. */
    async importLotEntries(companyId: number, rows: unknown[]): Promise<any> {
        return this.post('/item-master/lot-entries', { companyId, rows });
    }

    /** Bin locations for a warehouse (empty when the warehouse is not bin-managed). */
    async getWarehouseBins(companyId: number, warehouse: string): Promise<any> {
        return this.get('/warehouse/bins', { companyId, warehouse });
    }

    /** GRPOs already created against a PO (headers from the sap-grpo sync; batch/bin empty). */
    async getWarehousePoGrpos(companyId: number, poDocEntry: number): Promise<any> {
        return this.get('/warehouse/grpos', { companyId, poDocEntry });
    }

    /** One GRPO's full document live from SAP (populated batch/expiry/bin). */
    async getWarehouseGrpoDetail(companyId: number, docEntry: number, poDocEntry?: number): Promise<any> {
        return this.get('/warehouse/grpo-detail', { companyId, docEntry, ...(poDocEntry ? { poDocEntry } : {}) });
    }

    /** Submit a GRPO (PurchaseDeliveryNotes) to SAP against the anchored PO. */
    async submitWarehouseGrpo(
        companyId: number,
        sourceDocEntry: number,
        lines: unknown[],
    ): Promise<any> {
        return this.post('/warehouse/grpo', { companyId, sourceDocEntry, lines });
    }

    /** Deliveries already created against a Sales Order (headers from sap-deliveries; batch/bin empty). */
    async getWarehouseSoDeliveries(companyId: number, soDocEntry: number): Promise<any> {
        return this.get('/warehouse/deliveries', { companyId, soDocEntry });
    }

    /** One Delivery's full document live from SAP (populated batch/bin). */
    async getWarehouseDeliveryDetail(companyId: number, docEntry: number, soDocEntry?: number): Promise<any> {
        return this.get('/warehouse/delivery-detail', { companyId, docEntry, ...(soDocEntry ? { soDocEntry } : {}) });
    }

    /** Deliverability check (warehouse availability + batch expiry) for delivery lines. */
    async getDeliveryStock(
        companyId: number,
        lines: Array<{ itemCode: string; warehouse?: string; batch?: string }>,
    ): Promise<any> {
        return this.post('/warehouse/delivery-stock', { companyId, lines });
    }

    /** Submit a Delivery (DeliveryNotes) to SAP against the anchored SO, optionally with an AR Invoice. */
    async submitWarehouseDelivery(
        companyId: number,
        sourceDocEntry: number,
        lines: unknown[],
        createInvoice: boolean,
    ): Promise<any> {
        return this.post('/warehouse/delivery', { companyId, sourceDocEntry, lines, createInvoice });
    }

    // ---- Warehouse document print-outs (PDF blobs) ----

    /** GRPO (Material Receipt Voucher) PDF. */
    async getWarehouseGrpoPdf(companyId: number, docEntry: number, poDocEntry?: number): Promise<Blob> {
        const res = await this.axiosInstance.get('/warehouse/print/grpo', {
            params: { companyId, docEntry, ...(poDocEntry ? { poDocEntry } : {}) },
            responseType: 'blob',
        });
        return res.data as Blob;
    }

    /** Delivery Order PDF. */
    async getWarehouseDeliveryPdf(companyId: number, docEntry: number, soDocEntry?: number): Promise<Blob> {
        const res = await this.axiosInstance.get('/warehouse/print/delivery', {
            params: { companyId, docEntry, ...(soDocEntry ? { soDocEntry } : {}) },
            responseType: 'blob',
        });
        return res.data as Blob;
    }

    /** AR Invoice PDF. */
    async getWarehouseInvoicePdf(
        companyId: number,
        docEntry: number,
        opts: { soDocNum?: string; deliveryDate?: string } = {},
    ): Promise<Blob> {
        const res = await this.axiosInstance.get('/warehouse/print/invoice', {
            params: { companyId, docEntry, ...(opts.soDocNum ? { soDocNum: opts.soDocNum } : {}), ...(opts.deliveryDate ? { deliveryDate: opts.deliveryDate } : {}) },
            responseType: 'blob',
        });
        return res.data as Blob;
    }

    // ---- Company report settings (logo + legal name + address for printed docs) ----
    async getCompanyReportSettings(companyId: number): Promise<any> {
        return this.get(`/companies/${companyId}/report-settings`);
    }
    /** Report settings; also the Formatted SOA's company details (empty = SAP's value). */
    async updateCompanyReportSettings(
        companyId: number,
        patch: {
            logo?: string | null; name?: string; address?: string; foreignName?: string; foreignAddress?: string;
            phone?: string; fax?: string; email?: string; website?: string; taxId?: string;
        },
    ): Promise<any> {
        return this.put(`/companies/${companyId}/report-settings`, patch);
    }

    // ---- Warehouse barcode maintenance (item-first link to SAP B1 OBCD) ----

    /** Barcodes already linked to an item (synced OBCD + pending local map). */
    async getItemBarcodes(companyId: number, itemCode: string): Promise<any> {
        return this.get('/warehouse/item-barcodes', { companyId, itemCode });
    }

    /** Item UoM options + default (a barcode in SAP must map to one UoM). */
    async getItemUoms(companyId: number, itemCode: string): Promise<any> {
        return this.get('/warehouse/item-uoms', { companyId, itemCode });
    }

    /** Which item (if any) a barcode is already linked to, locally. */
    async getBarcodeOwner(companyId: number, barcode: string): Promise<any> {
        return this.get('/warehouse/barcode-owner', { companyId, barcode });
    }

    /** Register barcode→item links in SAP OBCD (auto-syncs each back into the cache). */
    async submitItemBarcodes(
        companyId: number,
        entries: Array<{ itemCode: string; barcode: string; uomEntry?: number }>,
    ): Promise<any> {
        return this.post('/warehouse/item-barcodes', { companyId, entries });
    }

    // ---- Warehouse item seed (company-independent last-resort resolver) ----
    async listItemSeed(search?: string): Promise<any> {
        return this.get('/warehouse/seed', search ? { search } : undefined);
    }
    async createItemSeed(body: {
        itemNo: string; itemDescription?: string; itemGroup?: string; pic?: string; barcode?: string; gtin?: string; isActive?: boolean;
    }): Promise<any> {
        return this.post('/warehouse/seed', body);
    }
    async updateItemSeed(id: number, body: {
        itemNo: string; itemDescription?: string; itemGroup?: string; pic?: string; barcode?: string; gtin?: string; isActive?: boolean;
    }): Promise<any> {
        return this.put(`/warehouse/seed/${id}`, body);
    }
    async deleteItemSeed(id: number): Promise<any> {
        return this.delete(`/warehouse/seed/${id}`);
    }

    // ---- MCSR (Monthly Cost Status Report) dashboard — all read-only ----
    async getMcsrProjects(): Promise<any> {
        return this.get('/mcsr/projects');
    }
    async getMcsrCustomers(): Promise<any> {
        return this.get('/mcsr/customers');
    }
    async getMcsrMonths(project?: string): Promise<any> {
        return this.get('/mcsr/months', project ? { project } : undefined);
    }
    async getMcsrReport(project: string, fdate: string, tdate: string): Promise<any> {
        return this.get('/mcsr/report', { project, fdate, tdate });
    }
    async getMcsrOverview(fdate: string, tdate: string): Promise<any> {
        return this.get('/mcsr/overview', { fdate, tdate });
    }
    async getMcsrTrend(project: string, fdate: string, tdate: string): Promise<any> {
        return this.get('/mcsr/trend', { project, fdate, tdate });
    }
    async getMcsrTransactions(project: string, fdate: string, tdate: string, ocrcode3?: string): Promise<any> {
        return this.get('/mcsr/transactions', { project, fdate, tdate, ...(ocrcode3 ? { ocrcode3 } : {}) });
    }
    async getMcsrLiveExpenditure(project: string, ocrcode3: string, fdate: string, tdate: string, costtype: '1' | '2' | '3'): Promise<any> {
        return this.get('/mcsr/live-expenditure', { project, ocrcode3, fdate, tdate, costtype });
    }
    /** Server-rendered exports (pixel-faithful workbook / landscape PDF). */
    async getMcsrSyncStatus(): Promise<any> {
        return this.get('/mcsr/sync/status');
    }
    async runMcsrSync(scheduleIds?: number[]): Promise<any> {
        // POSTs don't get companyId auto-added by the request interceptor.
        const selectedCompanyId = localStorage.getItem('selectedCompanyId');
        return this.post('/mcsr/sync/run', {
            companyId: selectedCompanyId ? Number(selectedCompanyId) : undefined,
            ...(scheduleIds && scheduleIds.length ? { scheduleIds } : {}),
        });
    }
    async downloadMcsrExport(kind: 'xlsx' | 'pdf', project: string, fdate: string, tdate: string): Promise<Blob> {
        const companyId = Number(localStorage.getItem('selectedCompanyId') || '');
        const response = await this.axiosInstance.get(`/mcsr/export/${kind}`, {
            params: { companyId, project, fdate, tdate },
            responseType: 'blob',
        });
        return response.data as Blob;
    }

    // ---- MCSR bulk print and export (backend routes/mcsr.routes.ts /bulk/*) ----
    async getMcsrBulkProjects(month: string): Promise<any> {
        return this.get('/mcsr/bulk/projects', { month }, { timeout: 120000 });
    }
    async startMcsrBulkRun(body: { month: string; projects: string[]; parentsOnly: boolean; outputs: { pdf: boolean; zip: boolean; xlsx: boolean } }): Promise<any> {
        // POSTs don't get companyId auto-added by the request interceptor.
        const companyId = Number(localStorage.getItem('selectedCompanyId') || '');
        return this.post('/mcsr/bulk/runs', { companyId, ...body }, { timeout: 120000 });
    }
    async getMcsrBulkRuns(): Promise<any> {
        return this.get('/mcsr/bulk/runs');
    }
    async cancelMcsrBulkRun(runId: number): Promise<any> {
        return this.post(`/mcsr/bulk/runs/${runId}/cancel`);
    }
    async downloadMcsrBulkRun(runId: number, kind: 'pdf' | 'zip' | 'xlsx'): Promise<{ blob: Blob; fileName: string }> {
        const response = await this.axiosInstance.get(`/mcsr/bulk/runs/${runId}/download`, {
            params: { kind }, responseType: 'blob', timeout: 600000,
        });
        const disposition = String(response.headers['content-disposition'] ?? '');
        const fileName = /filename\*?=(?:UTF-8'')?"?([^";]+)"?/i.exec(disposition)?.[1] ?? `MCSR.${kind}`;
        return { blob: response.data as Blob, fileName: decodeURIComponent(fileName) };
    }

    // ---- Projects > Monthly Reports / Project Issues (backend routes/projectReport.routes.ts) ----
    async getProjectReportConfig(companyId: number): Promise<any> {
        return this.get('/project-reports/config', { companyId });
    }

    async getProjectReportMonth(companyId: number, month: string): Promise<any> {
        return this.get('/project-reports/month', { companyId, month }, { timeout: 60000 });
    }

    /** Reads SAP and MCSR for the new report: allow time. */
    async createProjectReport(companyId: number, projectCode: string, month: string): Promise<any> {
        return this.post('/project-reports', { companyId, projectCode, month }, { timeout: 180000 });
    }

    async getProjectReport(reportId: number): Promise<any> {
        return this.get(`/project-reports/${reportId}`);
    }

    async saveProjectReport(reportId: number, body: { data: unknown; issues: unknown; reportNo?: number }): Promise<any> {
        return this.put(`/project-reports/${reportId}`, body);
    }

    async refreshProjectReport(reportId: number): Promise<any> {
        return this.post(`/project-reports/${reportId}/refresh`, {}, { timeout: 180000 });
    }

    /** Saves the given data and issues, then submits. */
    async submitProjectReport(reportId: number, body: { data: unknown; issues: unknown; reportNo?: number }): Promise<any> {
        return this.post(`/project-reports/${reportId}/submit`, body);
    }

    async returnProjectReport(reportId: number, note: string): Promise<any> {
        return this.post(`/project-reports/${reportId}/return`, { note });
    }

    async deleteProjectReport(reportId: number): Promise<any> {
        return this.delete(`/project-reports/${reportId}`);
    }

    async downloadProjectReportPdf(reportId: number): Promise<Blob> {
        const response = await this.axiosInstance.get(`/project-reports/${reportId}/pdf`, { responseType: 'blob', timeout: 120000 });
        return response.data as Blob;
    }

    async getProjectIssues(params: Record<string, string | number | boolean | undefined>): Promise<any> {
        return this.get('/project-reports/issues', params, { timeout: 60000 });
    }

    // ---- Formatted SOA (Sales Portal > Formatted SOA and Statement Templates; docs/FORMATTED_SOA.md) ----
    async getSoaConfig(companyId: number): Promise<any> {
        return this.get('/formatted-soa/config', { companyId });
    }

    /** Customers or suppliers the user may print: a search, or (withBalance) every one with a SAP balance. */
    async getSoaPartners(companyId: number, type: 'C' | 'S', search: string, withBalance = false): Promise<any> {
        return this.get('/formatted-soa/partners', { companyId, type, search, withBalance: withBalance || undefined }, { timeout: 180000 });
    }

    async getSoaTemplates(companyId: number): Promise<any> {
        return this.get('/formatted-soa/templates', { companyId });
    }

    async getSoaTemplate(templateId: number): Promise<any> {
        return this.get(`/formatted-soa/templates/${templateId}`);
    }

    async createSoaTemplate(body: {
        companyId: number; name: string; description?: string; appliesTo?: string; definition?: unknown; from?: 'standard' | 'blank';
    }): Promise<any> {
        return this.post('/formatted-soa/templates', body);
    }

    async updateSoaTemplate(templateId: number, patch: { name?: string; description?: string; appliesTo?: string; definition?: unknown }): Promise<any> {
        return this.put(`/formatted-soa/templates/${templateId}`, patch);
    }

    async deleteSoaTemplate(templateId: number): Promise<any> {
        return this.delete(`/formatted-soa/templates/${templateId}`);
    }

    async setDefaultSoaTemplate(templateId: number): Promise<any> {
        return this.post(`/formatted-soa/templates/${templateId}/default`);
    }

    async copySoaTemplate(templateId: number, targetCompanyIds: number[], name?: string): Promise<any> {
        return this.post(`/formatted-soa/templates/${templateId}/copy`, { targetCompanyIds, name });
    }

    async getSoaTemplateHistory(templateId: number): Promise<any> {
        return this.get(`/formatted-soa/templates/${templateId}/history`);
    }

    async restoreSoaTemplateVersion(templateId: number, historyId: number): Promise<any> {
        return this.post(`/formatted-soa/templates/${templateId}/history/${historyId}/restore`);
    }

    /** The statement document (HTML) for the sandboxed preview frame; reads SAP for a real BP. */
    async previewSoa(body: Record<string, unknown>): Promise<any> {
        return this.post('/formatted-soa/preview', body, { timeout: 180000 });
    }

    /** PDF of 1 to 25 statements; `notes` = the layout warnings the renderer reported. */
    async downloadSoaPdf(body: Record<string, unknown>): Promise<{ blob: Blob; fileName: string; notes: string[] }> {
        const response = await this.axiosInstance.post('/formatted-soa/pdf', body, { responseType: 'blob', timeout: 600000 });
        const disposition = String(response.headers['content-disposition'] ?? '');
        const fileName = /filename="([^"]+)"/.exec(disposition)?.[1] ?? 'Statement.pdf';
        let notes: string[] = [];
        try {
            const raw = response.headers['x-soa-notes'];
            notes = raw ? JSON.parse(decodeURIComponent(String(raw))) : [];
        } catch {
            notes = [];
        }
        return { blob: response.data as Blob, fileName, notes };
    }

    async startSoaRun(body: {
        companyId: number; templateId?: number; cardType: 'C' | 'S'; cardCodes: string[]; fromDate: string; toDate: string;
        basis?: 'invoice' | 'due'; zip: boolean;
    }): Promise<any> {
        return this.post('/formatted-soa/runs', body, { timeout: 60000 });
    }

    async getSoaRuns(companyId: number): Promise<any> {
        return this.get('/formatted-soa/runs', { companyId });
    }

    async getSoaRun(runId: number): Promise<any> {
        return this.get(`/formatted-soa/runs/${runId}`);
    }

    async cancelSoaRun(runId: number): Promise<any> {
        return this.post(`/formatted-soa/runs/${runId}/cancel`);
    }

    async downloadSoaRun(runId: number, kind: 'pdf' | 'zip'): Promise<{ blob: Blob; fileName: string }> {
        const response = await this.axiosInstance.get(`/formatted-soa/runs/${runId}/download`, {
            params: { kind }, responseType: 'blob', timeout: 600000,
        });
        const disposition = String(response.headers['content-disposition'] ?? '');
        const fileName = /filename\*?=(?:UTF-8'')?"?([^";]+)"?/i.exec(disposition)?.[1] ?? `Statements.${kind}`;
        return { blob: response.data as Blob, fileName: decodeURIComponent(fileName) };
    }
}
// Assign instance to a variable to avoid anonymous default export warning
const apiService = new ApiService();
export default apiService;
